import { useEffect, useLayoutEffect, useState, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
    ResponsiveContainer, ComposedChart, Area, Line, Bar, BarChart,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import { useAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";
import { useDrillHistoryBack } from "../../hooks/useDrillHistoryBack";
import { useSwipeBack } from "../../hooks/useSwipeBack";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar from "../layout/TopBar";
import BottomNav from "../BottomNav";
import { useSharedReportDateFilter } from "../../redux/reportFilters/reportFiltersHooks";
import { useCurrencySymbol } from "../../utils/currency";
import { getLocationDetails } from "../../services/api";
import { getLoyaltySummary, getLoyaltyPointsFlowTrend, getLoyaltyMembersGrowth, getLoyaltyTopMembers, getLoyaltyTransactionBreakdown, getLoyaltyActivityLog, getLoyaltyAttributedRevenue, getLoyaltyAovComparison, getLoyaltyMemberProfile, getStartEndFromFilter } from "../../services/reportsApi";
import "./reports.css";
import { T, useIsDesktop, Bone } from "./reportTokens";
import { ReportSectionExportButton } from "./components/ReportExportButton";
import { LOY_DOLLAR_PER_POINT } from "./loyaltyRedemption";

/* ─── Auth token lookup (mirrors OutletDropdown.tsx / useCheckInReports.ts) ── */
const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== "object" || depth > 3) return null;
    const tokenKeys = ["accessToken", "access_token", "token"];
    for (const key of tokenKeys) {
        if (obj[key] && typeof obj[key] === "string" && obj[key].length > 10) return obj[key];
    }
    for (const val of Object.values(obj)) {
        if (val && typeof val === "object" && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

// Same gate as OutletDropdown.tsx (sidebar/topbar outlet switcher) — only these roles
// may switch outlets; everyone else sees a read-only label.
const SUPER_ROLES = ["Restaurant_Owner", "Regional_Employee", "Magil_Admin"];

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — LOYALTY REPORT  (LOY-RPT)
   Reports Portal analytics layer over the points-based loyalty program.
   Responsive: Desktop (≥900px) + Mobile (<900px). Frontend + sample data only.
   The report OBSERVES; member actions & program config live in the Loyalty
   Admin Portal (/loyalty/*) which this report deep-links out to.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─── GA4 (no backend — pushes to dataLayer if present) ──────────────────── */
const track = (event: string, params: Record<string, any> = {}) => {
    try {
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({
            event, report_group: "Marketing", report_name: "Loyalty",
            platform: window.innerWidth >= 900 ? "desktop" : "mobile", ...params,
        });
    } catch { /* noop */ }
};

/* ─── Program configuration (read from loyalty program in a real build) ──── */
const PROGRAM = {
    dollarValuePerPoint: LOY_DOLLAR_PER_POINT,
    pointsPerDollar: 1,
    subtotalBasis: "Before Discounts" as "Before Discounts" | "After Discounts",
    expirationEnabled: true,
    expirationWarningDays: 30,
};

/* ─── Formatting ──────────────────────────────────────────────────────────── */
const fmtNumber = (n: number): string => Math.round(n || 0).toLocaleString();
// Same rounding as fmtNumber, but returns a number — for CSV/PDF export cells, which need
// the exported value to match what's on screen (screen uses fmtNumber) while staying a
// real number rather than a comma-formatted string (numeric columns must stay summable in CSV).
const roundNum = (n: number): number => Math.round(n || 0);
const fmtMoney = (n: number, symbol = "$"): string =>
    `${symbol}${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMoneyShort = (n: number, symbol = "$"): string => {
    const v = n || 0;
    if (Math.abs(v) >= 1000) return `${symbol}${(v / 1000).toFixed(1)}k`;
    return fmtMoney(v, symbol);
};
const isoDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// withYear=true for standalone dates like Last Activity, where the year is meaningful
// context; chart bucket labels (the other callers) stay year-less — axis space is tight
// and every point is already within the same selected range.
const fmtDayLabel = (iso: string, withYear = false): string => {
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return iso;
    return withYear ? `${MONTHS[m - 1]} ${d}, ${y}` : `${MONTHS[m - 1]} ${d}`;
};
const fmtDateTime = (iso: string): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    let h = date.getHours();
    const min = String(date.getMinutes()).padStart(2, "0");
    const ap = h >= 12 ? "PM" : "AM";
    h = h % 12; if (h === 0) h = 12;
    return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} ${h}:${min} ${ap}`;
};
const maskContact = (raw: string): string => {
    // Masks an email or phone consistent with the member directory.
    if (raw.includes("@")) {
        const [name, domain] = raw.split("@");
        const head = name.slice(0, 2);
        return `${head}${"•".repeat(Math.max(2, name.length - 2))}@${domain}`;
    }
    const digits = raw.replace(/\D/g, "");
    return `•••-•••-${digits.slice(-4)}`;
};

/* ─── Deterministic pseudo-random (stable data across renders) ───────────── */
const hashStr = (s: string): number => {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
};
const mulberry32 = (seed: number) => () => {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/* ─── Date range helpers ─────────────────────────────────────────────────── */
const _DAY = 86400000;
const _startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const _addDays = (d: Date, n: number) => new Date(d.getTime() + n * _DAY);

interface Range { start: Date; end: Date; label: string; }
function rangeForPreset(period: string): { cur: Range; prior: Range; priorLabel: string } {
    const today = _startOfDay(new Date());
    const fmt = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;
    const build = (start: Date, end: Date, label: string, priorLabel: string): { cur: Range; prior: Range; priorLabel: string } => {
        const spanDays = Math.round((end.getTime() - start.getTime()) / _DAY) + 1;
        const priorEnd = _addDays(start, -1);
        const priorStart = _addDays(priorEnd, -(spanDays - 1));
        return { cur: { start, end, label }, prior: { start: priorStart, end: priorEnd, label: "" }, priorLabel };
    };
    if (period.startsWith("custom:")) {
        const [, from, to] = period.split(":");
        const start = _startOfDay(new Date(from + "T12:00:00"));
        const end = _startOfDay(new Date(to + "T12:00:00"));
        return build(start, end, `${fmt(start)} – ${fmt(end)}`, "vs prior period");
    }
    // Multi-day presets end at YESTERDAY to match the portal-wide window
    // convention (services/reportsApi getStartEndFromFilter), so the Loyalty
    // Report reconciles with the Sales Report for the same preset.
    const yesterday = _addDays(today, -1);
    switch (period) {
        case "Yesterday": return build(yesterday, yesterday, "Yesterday", "vs previous day");
        case "7D": return build(_addDays(today, -7), yesterday, "Last 7 Days", "vs prev 7 days");
        case "90D": return build(_addDays(today, -90), yesterday, "Last 90 Days", "vs prev 90 days");
        case "This Month": {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = yesterday.getTime() < start.getTime() ? start : yesterday;
            return build(start, end, "This Month", "vs last month");
        }
        case "Today": return build(today, today, "Today", "vs yesterday");
        case "30D":
        default: return build(_addDays(today, -30), yesterday, "Last 30 Days", "vs prev 30 days");
    }
}

// Deltas compare the selected period against its prior window. Only the
// fixed-length presets have an unambiguous "prior period"; This Month (partial,
// month-to-date) and open-ended Custom ranges don't, so — matching Snapshot / Sales
// — show deltas for Today / Yesterday / 7D / any single hand-picked day, and hide
// them everywhere else. A single day picked from the calendar arrives as a
// `custom:YYYY-MM-DD:YYYY-MM-DD` token rather than the literal "Yesterday" token,
// but the API resolves and returns the exact same well-defined day-over-day delta
// for it, so it must not be excluded just because the token string doesn't match.
const showDeltaForPeriod = (period: string, startDate: string | null, endDate: string | null): boolean =>
    period === "Today" || period === "Yesterday" || period === "7D" || (!!startDate && startDate === endDate);

/* ═══════════════════════════════════════════════════════════════════════════
   LIVE DATA — loyalty-summary API (stamp KPI grid)
   GET /sales/loyalty/loyalty-summary?locationId=&startDate=&endDate=
   ═══════════════════════════════════════════════════════════════════════════ */
interface LoyaltySummary {
    enrolledMembers: number; enrolledMembersTotal: number; enrolledMembersDelta: number | null;
    activeMembers: number; activeMembersDelta: number | null;
    // Points + their dollar value are both API-reported (not client-computed) — the API
    // is the source of truth for Dollar Value per Point, which is program-configured and
    // can vary; multiplying by a hardcoded rate client-side would drift from it.
    pointsIssued: number; pointsIssuedPrice: number; pointsIssuedDelta: number | null;
    pointsRedeemed: number; pointsRedeemedPrice: number; pointsRedeemedDelta: number | null;
    pointsExpired: number; pointsExpiredPrice: number; pointsExpiredDelta: number | null;
    outstandingPoints: number; outstandingPrice: number; redemptionRate: number | null; avgPointsBalance: number;
}

function useLoyaltySummary(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, dollarValuePerPoint: number, token: string | null) {
    const [summary, setSummary] = useState<LoyaltySummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!locationId || !startDate || !endDate) { setSummary(null); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltySummary({ locationId, ...(isToday ? {} : { startDate, endDate }), dollarValuePerPoint, token })
            .then((res: any) => { if (!cancelled) setSummary(res ?? null); })
            .catch((err: any) => { if (!cancelled) { setError(err?.message || "Failed to load loyalty summary"); setSummary(null); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, isToday, dollarValuePerPoint, token]);

    return { summary, loading, error };
}

/* ─── Points Flow Trend (live) — GET /sales/loyalty/points-flow-trend ────── */
interface PointsFlowDay { date: string; pointsIssued: number; pointsRedeemed: number; }
interface PointsFlowTrend { totalPointsIssued: number; totalPointsRedeemed: number; dailyTrend: PointsFlowDay[]; }

function usePointsFlowTrend(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, token: string | null) {
    const [trend, setTrend] = useState<PointsFlowTrend | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!locationId || !startDate || !endDate) { setTrend(null); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltyPointsFlowTrend({ locationId, ...(isToday ? {} : { startDate, endDate }), token })
            .then((res: any) => { if (!cancelled) setTrend(res ?? null); })
            .catch((err: any) => { if (!cancelled) { setError(err?.message || "Failed to load points flow trend"); setTrend(null); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, isToday, token]);

    return { trend, loading, error };
}

// Bucketing mirrors the mock chart convention: daily for ≤31 days, weekly otherwise.
function buildFlowSeriesFromTrend(trend: PointsFlowTrend | null): { flowSeries: { label: string; issued: number; redeemed: number }[]; bucketNote: string; isEmpty: boolean } {
    const daily = trend?.dailyTrend ?? [];
    const bucketWeekly = daily.length > 31;
    const bucketNote = bucketWeekly ? "Weekly buckets" : "Daily buckets";
    const flowSeries: { label: string; issued: number; redeemed: number }[] = [];
    if (bucketWeekly) {
        for (let i = 0; i < daily.length; i += 7) {
            const chunk = daily.slice(i, i + 7);
            flowSeries.push({
                label: fmtDayLabel(chunk[0].date),
                issued: chunk.reduce((a, d) => a + d.pointsIssued, 0),
                redeemed: chunk.reduce((a, d) => a + d.pointsRedeemed, 0),
            });
        }
    } else {
        for (const d of daily) flowSeries.push({ label: fmtDayLabel(d.date), issued: d.pointsIssued, redeemed: d.pointsRedeemed });
    }
    const isEmpty = (trend?.totalPointsIssued ?? 0) === 0 && (trend?.totalPointsRedeemed ?? 0) === 0;
    return { flowSeries, bucketNote, isEmpty };
}

/* ─── Members Growth (live) — GET /sales/loyalty/members-growth ──────────── */
interface MembersGrowthDay { date: string; newMembers: number; cumulativeMembers: number; }
interface MembersGrowthTrend { totalNewMembers: number; cumulativeMembers: number; dailyTrend: MembersGrowthDay[]; }

function useMembersGrowthTrend(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, token: string | null) {
    const [trend, setTrend] = useState<MembersGrowthTrend | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!locationId || !startDate || !endDate) { setTrend(null); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltyMembersGrowth({ locationId, ...(isToday ? {} : { startDate, endDate }), token })
            .then((res: any) => { if (!cancelled) setTrend(res ?? null); })
            .catch((err: any) => { if (!cancelled) { setError(err?.message || "Failed to load members growth"); setTrend(null); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, isToday, token]);

    return { trend, loading, error };
}

// Bucketing mirrors the Points Flow convention: daily for ≤31 days, weekly otherwise.
// Cumulative is already running-total from the API, so a weekly bucket takes the
// chunk's last cumulative value rather than summing it.
function buildGrowthSeriesFromTrend(trend: MembersGrowthTrend | null): { growthSeries: { label: string; newMembers: number; cumulative: number }[]; bucketNote: string } {
    const daily = trend?.dailyTrend ?? [];
    const bucketWeekly = daily.length > 31;
    const bucketNote = bucketWeekly ? "Weekly buckets" : "Daily buckets";
    const growthSeries: { label: string; newMembers: number; cumulative: number }[] = [];
    if (bucketWeekly) {
        for (let i = 0; i < daily.length; i += 7) {
            const chunk = daily.slice(i, i + 7);
            growthSeries.push({
                label: fmtDayLabel(chunk[0].date),
                newMembers: chunk.reduce((a, d) => a + d.newMembers, 0),
                cumulative: chunk[chunk.length - 1].cumulativeMembers,
            });
        }
    } else {
        for (const d of daily) growthSeries.push({ label: fmtDayLabel(d.date), newMembers: d.newMembers, cumulative: d.cumulativeMembers });
    }
    return { growthSeries, bucketNote };
}

/* ─── Transaction Breakdown (live) — GET /sales/loyalty/transaction-breakdown ─ */
interface TxnBreakdownRow { transactionType: string; points: number; }
interface TxnBreakdownResponse { totalPoints: number; breakdown: TxnBreakdownRow[]; }

function useTransactionBreakdown(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, token: string | null) {
    const [breakdown, setBreakdown] = useState<TxnBreakdownResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!locationId || !startDate || !endDate) { setBreakdown(null); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltyTransactionBreakdown({ locationId, ...(isToday ? {} : { startDate, endDate }), token })
            .then((res: any) => { if (!cancelled) setBreakdown(res ?? null); })
            .catch((err: any) => { if (!cancelled) { setError(err?.message || "Failed to load transaction breakdown"); setBreakdown(null); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, isToday, token]);

    return { breakdown, loading, error };
}

// Backend has been observed sending both underscore ("adjust_credit") and
// space-separated ("adjust credit") forms for the same transaction type, so every
// lookup against `transactionType` goes through this normalizer first rather than
// keying on the raw string.
const normalizeTxnType = (raw: string): string => (raw || "").toLowerCase().trim().replace(/[_\s]+/g, " ");

const TXN_TYPE_LABELS: Record<string, string> = { earn: "Earn", redeem: "Redeem", expiration: "Expire", "adjust debit": "Adjust", "adjust credit": "Adjust", refund: "Refund", reversal: "Reversal" };
const TXN_TYPE_COLORS: Record<string, string> = { earn: T.green, redeem: T.accent, expiration: T.orange, "adjust debit": T.blue, "adjust credit": T.blue, refund: T.teal, reversal: T.purple };

function buildBreakdownFromApi(res: TxnBreakdownResponse | null): { breakdown: { type: string; points: number; color: string }[]; isEmpty: boolean } {
    const rows = res?.breakdown ?? [];
    const breakdown = rows
        .filter((r) => r.points > 0)
        .map((r) => ({
            type: TXN_TYPE_LABELS[normalizeTxnType(r.transactionType)] ?? r.transactionType,
            points: r.points,
            color: TXN_TYPE_COLORS[normalizeTxnType(r.transactionType)] ?? T.t3,
        }))
        .sort((a, b) => b.points - a.points);
    const isEmpty = (res?.totalPoints ?? 0) === 0;
    return { breakdown, isEmpty };
}

/* ─── Loyalty-Attributed Revenue (live) — GET /sales/loyalty/attributed-revenue ─ */
interface AttributedRevenueResponse { attributedRevenue: number; attributedRevenueDelta: number | null; memberOrderCount: number; }

function useAttributedRevenue(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, token: string | null) {
    const [data, setData] = useState<AttributedRevenueResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!locationId || !startDate || !endDate) { setData(null); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltyAttributedRevenue({ locationId, ...(isToday ? {} : { startDate, endDate }), token })
            .then((res: any) => { if (!cancelled) setData(res ?? null); })
            .catch((err: any) => { if (!cancelled) { setError(err?.message || "Failed to load attributed revenue"); setData(null); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, isToday, token]);

    return { data, loading, error };
}

/* ─── Loyalty vs Non-Loyalty AOV (live) — GET /sales/loyalty/aov-comparison ─ */
interface AovComparisonResponse { loyaltyMemberAov: number | null; nonMemberAov: number | null; aovLiftPercentage: number | null; }

function useAovComparison(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, token: string | null) {
    const [data, setData] = useState<AovComparisonResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!locationId || !startDate || !endDate) { setData(null); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltyAovComparison({ locationId, ...(isToday ? {} : { startDate, endDate }), token })
            .then((res: any) => { if (!cancelled) setData(res ?? null); })
            .catch((err: any) => { if (!cancelled) { setError(err?.message || "Failed to load AOV comparison"); setData(null); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, isToday, token]);

    return { data, loading, error };
}

/* ─── Top Members (live) — GET /sales/loyalty/top-members ────────────────── */
interface TopMemberApiRow {
    loyaltyCustomerId: string; memberName: string; maskedContact: string;
    enrollmentChannel: string; currentBalance: number; lifetimeEarned: number;
    lifetimeRedeemed: number; orders: number; lastActivity: string;
}
interface TopMembersPage { content: TopMemberApiRow[]; page: number; size: number; totalElements: number; totalPages: number; }

function mapTopMember(r: TopMemberApiRow): LoyMember {
    return {
        id: r.loyaltyCustomerId, name: r.memberName, contact: r.maskedContact,
        channel: (r.enrollmentChannel || "").toUpperCase(),
        balance: r.currentBalance, lifetimeEarned: r.lifetimeEarned, lifetimeRedeemed: r.lifetimeRedeemed,
        orders: r.orders, lastActivity: r.lastActivity,
    };
}

// Genuinely server-paginated: the visible page is exactly what's fetched — clicking
// Prev/Next, changing rows-per-page, re-sorting by Balance, or typing a search term all
// dispatch a fresh GET with the corresponding page/size/sortOrder/search. `loading` is
// scoped to "a fetch is in flight" (drives the table-only dim); `initialLoading` stays
// true only until the very first fetch settles, so the report's whole-page shimmer isn't
// re-shown on every later page click. startDate/endDate are forwarded like every other
// loyalty endpoint so switching the report's date filter refetches this section too, even
// though the ranking itself is a point-in-time balance leaderboard, not period-scoped.
function useTopMembers(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, token: string | null) {
    const [members, setMembers] = useState<LoyMember[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0); // 0-indexed for the UI; sent as page+1 to the API
    const [pageSize, setPageSize] = useState(10);
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Any change that invalidates the current page resets back to page 0.
    useEffect(() => { setPage(0); }, [locationId, startDate, endDate, pageSize, sortDir, debouncedSearch]);

    useEffect(() => {
        if (!locationId) { setMembers([]); setTotalElements(0); setLoading(false); setInitialLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltyTopMembers({
            locationId, ...(isToday ? {} : { startDate, endDate }), sortBy: "balance", sortOrder: sortDir,
            page: page + 1, size: pageSize,
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            token,
        })
            .then((res: TopMembersPage) => {
                if (cancelled) return;
                setMembers((res?.content ?? []).map(mapTopMember));
                setTotalElements(res?.totalElements ?? 0);
            })
            .catch((err: any) => {
                if (cancelled) return;
                setError(err?.message || "Failed to load top members");
                setMembers([]); setTotalElements(0);
            })
            .finally(() => { if (!cancelled) { setLoading(false); setInitialLoading(false); } });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, isToday, token, page, pageSize, sortDir, debouncedSearch]);

    return { members, totalElements, loading, initialLoading, error, page, setPage, pageSize, setPageSize, sortDir, setSortDir, search, setSearch };
}

// CSV/PDF export needs the FULL ranked list, not just the visible page (report-export
// convention: refetch with size = totalElements). A small first call learns the total,
// then one follow-up call sized to it if there's more than a page's worth.
async function fetchAllTopMembersForExport(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, sortDir: "asc" | "desc", search: string, token: string | null): Promise<LoyMember[]> {
    if (!locationId) return [];
    const base = { locationId, ...(isToday ? {} : { startDate, endDate }), sortBy: "balance", sortOrder: sortDir, ...(search ? { search } : {}), token };
    const first: TopMembersPage = await getLoyaltyTopMembers({ ...base, page: 1, size: 100 });
    const list = first?.content ?? [];
    const total = first?.totalElements ?? list.length;
    if (total <= list.length) return list.map(mapTopMember);
    const full: TopMembersPage = await getLoyaltyTopMembers({ ...base, page: 1, size: total });
    return (full?.content ?? list).map(mapTopMember);
}

/* ─── Loyalty Activity Log (live) — GET /sales/loyalty/activity-log ──────── */
interface ActivityLogApiRow {
    dateTime: string; memberName: string; loyaltyCustomerId: string; transactionType: string;
    points: number; balanceAfter: number; location: string; orderId: string | null; detail: string;
}
interface ActivityLogPage { content: ActivityLogApiRow[]; page: number; size: number; totalElements: number; totalPages: number; }

// Response rows use `transactionType` values "expiration" and "adjust debit"/"adjust credit"
// — the backend has been observed sending both underscore and space-separated forms of the
// adjust variants, so lookups go through normalizeTxnType() rather than keying on the raw
// string. The `type` filter query param uses the same vocabulary — keep both mappings in sync.
const API_TYPE_TO_TXN: Record<string, TxnType> = { earn: "Earn", redeem: "Redeem", expiration: "Expire", "adjust debit": "AdjustDebit", "adjust credit": "AdjustCredit", refund: "Refund", reversal: "Reversal" };
const TYPE_FILTER_TO_API: Record<Exclude<TxnType | "All", "All">, string> = { Earn: "earn", Redeem: "redeem", AdjustCredit: "adjust credit", AdjustDebit: "adjust debit", Expire: "expiration", Refund: "refund", Reversal: "reversal" };

function mapActivityLogRow(r: ActivityLogApiRow, i: number): LoyActivity {
    const type = API_TYPE_TO_TXN[normalizeTxnType(r.transactionType)] ?? "Earn";
    // "adjust debit" comes back from the API as a positive magnitude even though it's a
    // deduction — confirmed against balanceAfter (e.g. points: 25 but balance dropped by
    // 25). Force the sign here so every consumer (badge, CSV export) sees the true delta
    // rather than each having to know about this one type's quirk. "reversal" gets the
    // same treatment: it always reads as a deduction in the log regardless of the raw
    // API sign, so every consumer (points cell color, CSV export) shows it negative.
    const points = type === "AdjustDebit" || type === "Reversal" ? -Math.abs(r.points) : r.points;
    return {
        id: `${r.loyaltyCustomerId}-${r.dateTime}-${i}`,
        dateTime: r.dateTime, member: r.memberName, memberId: r.loyaltyCustomerId,
        type, points, balanceAfter: r.balanceAfter, location: r.location, detail: r.detail,
    };
}

// Same server-paginated shape as Top Members. Date/Time and Points sorting (per
// LOY-RPT-P-5.2) is wired the same way Top Members confirms sortBy=balance — sortBy
// values "dateTime" | "points" mirror the API's own response field names. This is NOT
// yet confirmed against the backend; if the param is silently ignored, the sort arrow
// will show without actually reordering results. Verify with backend before relying on it.
type ActivityLogSortKey = "dateTime" | "points";
function useActivityLog(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, token: string | null) {
    const [rows, setRows] = useState<LoyActivity[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [typeFilter, setTypeFilter] = useState<TxnType | "All">("All");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    // Default matches the spec: "sorted by Date/Time descending by default." sortBy stays
    // null in that default state so the backend's own default ordering applies rather than
    // forcing a param the backend may not expect.
    const [sortBy, setSortBy] = useState<ActivityLogSortKey | null>(null);
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => { setPage(0); }, [locationId, startDate, endDate, pageSize, typeFilter, debouncedSearch, sortBy, sortDir]);

    useEffect(() => {
        if (!locationId || !startDate || !endDate) { setRows([]); setTotalElements(0); setLoading(false); setInitialLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltyActivityLog({
            locationId, page: page + 1, size: pageSize,
            // Backend defaults to "today" when startDate/endDate are omitted, so skip them
            // for the Today preset rather than sending the redundant explicit range.
            ...(isToday ? {} : { startDate, endDate }),
            ...(typeFilter !== "All" ? { type: TYPE_FILTER_TO_API[typeFilter] } : {}),
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(sortBy ? { sortBy, sortOrder: sortDir } : {}),
            token,
        })
            .then((res: ActivityLogPage) => {
                if (cancelled) return;
                setRows((res?.content ?? []).map(mapActivityLogRow));
                setTotalElements(res?.totalElements ?? 0);
            })
            .catch((err: any) => {
                if (cancelled) return;
                setError(err?.message || "Failed to load activity log");
                setRows([]); setTotalElements(0);
            })
            .finally(() => { if (!cancelled) { setLoading(false); setInitialLoading(false); } });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, isToday, token, page, pageSize, typeFilter, debouncedSearch, sortBy, sortDir]);

    return { rows, totalElements, loading, initialLoading, error, page, setPage, pageSize, setPageSize, typeFilter, setTypeFilter, search, setSearch, sortBy, setSortBy, sortDir, setSortDir };
}

// CSV/PDF export needs the FULL filtered log, not just the visible page.
async function fetchAllActivityLogForExport(locationId: string | null, startDate: string | null, endDate: string | null, isToday: boolean, typeFilter: TxnType | "All", search: string, sortBy: ActivityLogSortKey | null, sortDir: "asc" | "desc", token: string | null): Promise<LoyActivity[]> {
    if (!locationId) return [];
    const base = {
        locationId,
        ...(isToday ? {} : { startDate, endDate }),
        ...(typeFilter !== "All" ? { type: TYPE_FILTER_TO_API[typeFilter] } : {}),
        ...(search ? { search } : {}),
        ...(sortBy ? { sortBy, sortOrder: sortDir } : {}),
        token,
    };
    const first: ActivityLogPage = await getLoyaltyActivityLog({ ...base, page: 1, size: 100 });
    const list = first?.content ?? [];
    const total = first?.totalElements ?? list.length;
    if (total <= list.length) return list.map(mapActivityLogRow);
    const full: ActivityLogPage = await getLoyaltyActivityLog({ ...base, page: 1, size: total });
    return (full?.content ?? list).map(mapActivityLogRow);
}

// Member Activity drill-in: same endpoint, scoped by loyaltyCustomerId instead of
// locationId/date range — this is a lifetime, program-wide log for one member, matching
// the "Balance is program-wide" messaging on the member detail card. No sortBy param is
// documented here either, so columns stay display-only, same reasoning as the main log.
function useMemberActivityLog(loyaltyCustomerId: string | null, token: string | null) {
    const [rows, setRows] = useState<LoyActivity[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => { setPage(0); }, [loyaltyCustomerId, pageSize, debouncedSearch]);

    useEffect(() => {
        if (!loyaltyCustomerId) { setRows([]); setTotalElements(0); setLoading(false); setInitialLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltyActivityLog({
            loyaltyCustomerId, page: page + 1, size: pageSize,
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            token,
        })
            .then((res: ActivityLogPage) => {
                if (cancelled) return;
                setRows((res?.content ?? []).map(mapActivityLogRow));
                setTotalElements(res?.totalElements ?? 0);
            })
            .catch((err: any) => {
                if (cancelled) return;
                setError(err?.message || "Failed to load member activity");
                setRows([]); setTotalElements(0);
            })
            .finally(() => { if (!cancelled) { setLoading(false); setInitialLoading(false); } });
        return () => { cancelled = true; };
    }, [loyaltyCustomerId, token, page, pageSize, debouncedSearch]);

    return { rows, totalElements, loading, initialLoading, error, page, setPage, pageSize, setPageSize, search, setSearch };
}

async function fetchAllMemberActivityLogForExport(loyaltyCustomerId: string | null, search: string, token: string | null): Promise<LoyActivity[]> {
    if (!loyaltyCustomerId) return [];
    const base = { loyaltyCustomerId, ...(search ? { search } : {}), token };
    const first: ActivityLogPage = await getLoyaltyActivityLog({ ...base, page: 1, size: 100 });
    const list = first?.content ?? [];
    const total = first?.totalElements ?? list.length;
    if (total <= list.length) return list.map(mapActivityLogRow);
    const full: ActivityLogPage = await getLoyaltyActivityLog({ ...base, page: 1, size: total });
    return (full?.content ?? list).map(mapActivityLogRow);
}

/* ─── Member profile (header + 5 stat cards on Member Detail) ───────────── */
interface MemberProfileResponse {
    loyaltyCustomerId: string;
    memberName: string;
    maskedContact: string;
    enrollmentChannel: string;
    lastActivity: string;
    currentBalance: number;
    lifetimeEarned: number;
    lifetimeRedeemed: number;
    orders: number;
    liabilityValue: number;
}
function useMemberProfile(loyaltyCustomerId: string | null, dollarValuePerPoint: number, token: string | null) {
    const [profile, setProfile] = useState<MemberProfileResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!loyaltyCustomerId) { setProfile(null); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getLoyaltyMemberProfile({ loyaltyCustomerId, dollarValuePerPoint, token })
            .then((res: any) => {
                if (cancelled) return;
                setProfile(res ?? null);
            })
            .catch((err: any) => {
                if (cancelled) return;
                setError(err?.message || "Failed to load member profile");
                setProfile(null);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [loyaltyCustomerId, dollarValuePerPoint, token]);

    return { profile, loading, error };
}

/* ─── Real branch/outlet list (merchant location details) ───────────────── */
interface OutletBranch { id: string; name: string; }
/** Sentinel for the "All outlets" row. Resolved to every branch id, comma
 *  separated, at the API boundary — the endpoints take `List<String> locationId`. */
const ALL_OUTLETS = "__all__";
function useOutletBranches(seedLocationId: string | null, token: string | null) {
    const [branches, setBranches] = useState<OutletBranch[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!seedLocationId || !token) return;
        let cancelled = false;
        setLoading(true);
        getLocationDetails(seedLocationId, token)
            .then((res: any) => {
                if (cancelled) return;
                const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
                const list = payload?.branch ?? payload?.branches ?? [];
                const mapped: OutletBranch[] = (Array.isArray(list) ? list : []).map((b: any) => ({
                    id: b.id ?? b.locationId, name: b.locationName ?? b.name ?? "Branch",
                }));
                setBranches(mapped);
            })
            .catch(() => { if (!cancelled) setBranches([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [seedLocationId, token]);

    return { branches, loading };
}

/* ─── Stamp KPI grid built from the live loyalty-summary response ───────── */
function buildKpisFromSummary(summary: LoyaltySummary | null, sym: string): { kpis: LoyaltyKpi[]; secondaryKpis: LoyaltyKpi[] } {
    const s: LoyaltySummary = summary ?? {
        enrolledMembers: 0, enrolledMembersTotal: 0, enrolledMembersDelta: null,
        activeMembers: 0, activeMembersDelta: null,
        pointsIssued: 0, pointsIssuedPrice: 0, pointsIssuedDelta: null,
        pointsRedeemed: 0, pointsRedeemedPrice: 0, pointsRedeemedDelta: null,
        pointsExpired: 0, pointsExpiredPrice: 0, pointsExpiredDelta: null,
        outstandingPoints: 0, outstandingPrice: 0, redemptionRate: 0, avgPointsBalance: 0,
    };
    const kpis: LoyaltyKpi[] = [
        {
            key: "totalMembers", label: "TOTAL MEMBERS", mobileLabel: "MEMBERS",
            value: fmtNumber(s.enrolledMembersTotal), secondary: `+${fmtNumber(s.enrolledMembers)} new`,
            delta: s.enrolledMembersDelta,
            info: "Everyone ever enrolled in your loyalty program at the selected outlet. The delta shows how many joined in the selected period.",
        },
        {
            key: "activeMembers", label: "ACTIVE MEMBERS", mobileLabel: "ACTIVE",
            value: fmtNumber(s.activeMembers), delta: s.activeMembersDelta,
            info: "Members who earned or redeemed points at least once during the selected period. A measure of engagement, not total membership.",
        },
        {
            key: "pointsIssued", label: "POINTS ISSUED", mobileLabel: "ISSUED",
            value: fmtNumber(s.pointsIssued), secondary: `${fmtMoneyShort(s.pointsIssuedPrice, sym)} value`,
            delta: s.pointsIssuedDelta,
            info: "Points earned from qualifying purchases in the selected period. Earned on the order subtotal using your program's Subtotal Basis setting. Tax and tips never earn points.",
        },
        {
            key: "pointsRedeemed", label: "POINTS REDEEMED", mobileLabel: "REDEEMED",
            value: fmtNumber(s.pointsRedeemed), secondary: `${fmtMoney(s.pointsRedeemedPrice, sym)} in rewards`,
            delta: s.pointsRedeemedDelta,
            info: "Points members spent on rewards during the selected period. The dollar figure is the discount those points delivered, valued at your Dollar Value per Point.",
        },
        {
            key: "liability", label: "OUTSTANDING LIABILITY", mobileLabel: "LIABILITY",
            value: fmtMoney(s.outstandingPrice, sym), secondary: `${fmtNumber(s.outstandingPoints)} pts outstanding`,
            delta: null, pointInTime: true, tertiary: "As of today",
            info: "The dollar amount you would owe if every unredeemed point were redeemed today.",
        },
        {
            key: "redemptionRate", label: "REDEMPTION RATE", mobileLabel: "REDEEM %",
            // Per spec: 0 lifetime points issued shows "—" rather than dividing by zero —
            // the API sends null for that case rather than 0, so guard before .toFixed().
            value: s.redemptionRate == null ? "—" : `${s.redemptionRate.toFixed(1)}%`,
            delta: null, pointInTime: true, tertiary: "Lifetime, all members",
            info: "The share of all points ever issued that have been redeemed.",
        },
    ];
    const secondaryKpis: LoyaltyKpi[] = [
        {
            key: "breakage", label: "EXPIRED POINTS", mobileLabel: "EXPIRED",
            value: fmtNumber(s.pointsExpired), secondary: `${fmtMoney(s.pointsExpiredPrice, sym)} released`,
            delta: s.pointsExpiredDelta, neutral: true,
            info: "Points that expired unredeemed in the selected period, and the liability released as a result.",
        },
        {
            key: "avgBalance", label: "AVG POINTS BALANCE", mobileLabel: "AVG BAL",
            value: fmtNumber(s.avgPointsBalance),
            delta: null, pointInTime: true, tertiary: "Point-in-time, per active member",
            info: "Average unredeemed points held per active member.",
        },
    ];
    return { kpis, secondaryKpis };
}

/* ─── Members + activity (deterministic sample directory) ────────────────── */
const FIRST = ["Ava", "Liam", "Noah", "Emma", "Olivia", "Mateo", "Sofia", "Ethan", "Isla", "Aria", "Kai", "Zoe", "Leo", "Mia", "Ravi", "Nora", "Omar", "Lena", "Jack", "Priya", "Diego", "Chloe", "Sam", "Yuki"];
const LAST = ["Carter", "Nguyen", "Patel", "Silva", "Kim", "Brooks", "Reyes", "Haddad", "Oconnor", "Rossi", "Flores", "Novak", "Bauer", "Mensah", "Costa", "Ahmed"];
const CHANNELS = ["POS", "ONLINE"];
export interface LoyMember {
    id: string; name: string; contact: string; channel: string;
    balance: number; lifetimeEarned: number; lifetimeRedeemed: number;
    orders: number; lastActivity: string;
}
function genMembers(outletId: string | null): LoyMember[] {
    const rng = mulberry32(hashStr("members|" + (outletId ?? "all")));
    const today = _startOfDay(new Date());
    const count = 24;
    const list: LoyMember[] = [];
    for (let i = 0; i < count; i++) {
        const name = `${FIRST[i % FIRST.length]} ${LAST[(i * 7) % LAST.length]}`;
        const earned = Math.round(2000 + rng() * 42000);
        const redeemed = Math.round(earned * (0.2 + rng() * 0.6));
        const balance = Math.max(0, earned - redeemed - Math.round(rng() * 800));
        const domain = rng() > 0.5 ? "gmail.com" : "outlook.com";
        const raw = rng() > 0.4
            ? `${FIRST[i % FIRST.length].toLowerCase()}.${LAST[(i * 7) % LAST.length].toLowerCase()}@${domain}`
            : `${Math.floor(2000000000 + rng() * 7999999999)}`;
        list.push({
            id: `mem_${1000 + i}`,
            name,
            contact: maskContact(raw),
            channel: CHANNELS[Math.floor(rng() * CHANNELS.length)],
            balance,
            lifetimeEarned: earned,
            lifetimeRedeemed: redeemed,
            orders: Math.round(4 + rng() * 120),
            lastActivity: isoDate(_addDays(today, -Math.floor(rng() * 40))),
        });
    }
    return list.sort((a, b) => b.balance - a.balance);
}

export type TxnType = "Earn" | "Redeem" | "AdjustCredit" | "AdjustDebit" | "Expire" | "Refund" | "Reversal";
export interface LoyActivity {
    id: string; dateTime: string; member: string; memberId: string;
    type: TxnType; points: number; balanceAfter: number; location: string; detail: string;
}
/* ═══════════════════════════════════════════════════════════════════════════
   AGGREGATED DATA MODEL
   ═══════════════════════════════════════════════════════════════════════════ */
export interface LoyaltyKpi {
    key: string; label: string; mobileLabel: string; value: string;
    secondary?: string; tertiary?: string; delta: number | null;
    invertDelta?: boolean; neutral?: boolean; pointInTime?: boolean; big?: boolean;
    info: string;
}
interface LoyaltyData {
    kpis: LoyaltyKpi[];
    secondaryKpis: LoyaltyKpi[];
    members: LoyMember[];
    rangeLabel: string; priorLabel: string;
}

function buildLoyaltyData(period: string, outletId: string | null, sym: string, summary: LoyaltySummary | null): LoyaltyData {
    const { cur, priorLabel } = rangeForPreset(period);
    const { kpis, secondaryKpis } = buildKpisFromSummary(summary, sym);
    const members = genMembers(outletId);

    return {
        kpis, secondaryKpis,
        members,
        rangeLabel: cur.label, priorLabel,
    };
}

/* ═══════════════════════════════════════════════════════════════════════════
   INFO TOOLTIP  (hover on desktop, tap popover on mobile; one open at a time)
   ═══════════════════════════════════════════════════════════════════════════ */
const INFO_EVENT = "loy-info-open";
let _infoSeq = 0;
const INFO_TIP_W = 240;
function InfoTip({ text, metric }: { text: string; metric: string }) {
    const isDesktop = useIsDesktop();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ left: number; top: number; place: "top" | "bottom"; caretLeft: number } | null>(null);
    const id = useRef(`info_${_infoSeq++}`).current;
    const ref = useRef<HTMLSpanElement>(null);
    const bubbleRef = useRef<HTMLDivElement>(null);

    // Position the bubble from the icon's viewport rect. Rendered in a portal on
    // <body> with position:fixed so it is never clipped by a card's overflow:hidden
    // or a table's overflow-x:auto. clientWidth (not innerWidth) excludes the vertical
    // scrollbar, so the clamp doesn't leave the bubble a scrollbar's-width past the
    // visible edge — the cause of the clipped-tooltip bug on the rightmost KPI cards.
    const compute = () => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const iconCenterX = r.left + r.width / 2;
        const viewportW = document.documentElement.clientWidth;
        const left = Math.max(8, Math.min(iconCenterX - INFO_TIP_W / 2, viewportW - INFO_TIP_W - 8));
        const place: "top" | "bottom" = r.top > 170 ? "top" : "bottom";
        setPos({ left, top: place === "top" ? r.top - 8 : r.bottom + 8, place, caretLeft: iconCenterX - left });
    };

    const show = () => {
        window.dispatchEvent(new CustomEvent(INFO_EVENT, { detail: id }));
        compute();
        setOpen(true);
        track("loyalty_info_tooltip_viewed", { metric_name: metric });
    };

    // Re-measure against the bubble's actual rendered size once it's in the DOM — text
    // length varies per metric, so the real height (not a fixed guess) decides whether
    // "top" placement truly clears the viewport, and this catches any rounding the first
    // estimate left a few px past the right edge.
    useLayoutEffect(() => {
        if (!open || !ref.current || !bubbleRef.current) return;
        const r = ref.current.getBoundingClientRect();
        const b = bubbleRef.current.getBoundingClientRect();
        const iconCenterX = r.left + r.width / 2;
        const viewportW = document.documentElement.clientWidth;
        const left = Math.max(8, Math.min(iconCenterX - b.width / 2, viewportW - b.width - 8));
        const place: "top" | "bottom" = r.top - b.height - 8 > 8 ? "top" : "bottom";
        setPos({ left, top: place === "top" ? r.top - 8 : r.bottom + 8, place, caretLeft: iconCenterX - left });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onOther = (e: Event) => { if ((e as CustomEvent).detail !== id) setOpen(false); };
        const onOutside = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        const onMove = () => setOpen(false); // close on scroll/resize so it never floats detached
        window.addEventListener(INFO_EVENT, onOther);
        document.addEventListener("mousedown", onOutside);
        window.addEventListener("scroll", onMove, true);
        window.addEventListener("resize", onMove);
        return () => {
            window.removeEventListener(INFO_EVENT, onOther);
            document.removeEventListener("mousedown", onOutside);
            window.removeEventListener("scroll", onMove, true);
            window.removeEventListener("resize", onMove);
        };
    }, [open, id]);

    // Light surface + border + caret, matching the shared KpiTooltip used by the other
    // reports (Snapshot, Sales, ...) exactly — was a solid dark, caret-less bubble here,
    // the one visual inconsistency. Caret sits on whichever edge faces the icon: bottom
    // of the bubble when placed above it ("top"), top of the bubble when placed below it.
    const bubble = open && pos ? createPortal(
        <div
            ref={bubbleRef}
            style={{
                position: "fixed", zIndex: 4000, left: pos.left, top: pos.top,
                transform: pos.place === "top" ? "translateY(-100%)" : "none",
                width: INFO_TIP_W, maxWidth: "calc(100vw - 16px)",
                background: T.surface, color: T.t2, border: `1px solid ${T.cardBorder}`,
                fontSize: 11.5, lineHeight: 1.55, fontWeight: 400, padding: "10px 12px", borderRadius: 10,
                boxShadow: "0 6px 20px rgba(0,0,0,0.14)",
                textTransform: "none", letterSpacing: 0, whiteSpace: "normal", pointerEvents: "none",
            }}
        >
            {text}
            <div style={{
                position: "absolute", [pos.place === "top" ? "bottom" : "top"]: -5, left: pos.caretLeft,
                transform: "translateX(-50%) rotate(45deg)", width: 8, height: 8, background: T.surface,
                ...(pos.place === "top"
                    ? { borderRight: `1px solid ${T.cardBorder}`, borderBottom: `1px solid ${T.cardBorder}` }
                    : { borderLeft: `1px solid ${T.cardBorder}`, borderTop: `1px solid ${T.cardBorder}` }),
            }} />
        </div>,
        document.body
    ) : null;

    return (
        <span
            ref={ref}
            onMouseEnter={isDesktop ? show : undefined}
            onMouseLeave={isDesktop ? () => setOpen(false) : undefined}
            onClick={(e) => { e.stopPropagation(); if (!isDesktop) (open ? setOpen(false) : show()); else show(); }}
            style={{ position: "relative", display: "inline-flex", alignItems: "center", cursor: "pointer", marginLeft: 4, verticalAlign: "middle" }}
        >
            <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t3, opacity: 0.85 }}>info</span>
            {bubble}
        </span>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI CARD
   ═══════════════════════════════════════════════════════════════════════════ */
function KPICard({ kpi, priorLabel, showDelta }: { kpi: LoyaltyKpi; priorLabel: string; showDelta: boolean }) {
    const isDesktop = useIsDesktop();
    const { label, mobileLabel, value, secondary, tertiary, delta, invertDelta, neutral, pointInTime, big } = kpi;
    const hasDelta = delta !== null && delta !== undefined;
    const safe = Number(delta) || 0;
    const isPositive = invertDelta ? safe < 0 : safe > 0;
    const deltaColor = neutral ? T.t2 : !hasDelta ? T.t3 : safe === 0 ? T.t3 : isPositive ? T.green : T.red;
    const deltaText = !hasDelta ? "" : safe === 0 ? "0%" : `${safe > 0 ? "↑" : "↓"} ${Math.abs(safe).toFixed(1)}%`;
    // Scale the value font to its length so long currency values (e.g. "$37,250.00")
    // never truncate in the tight 6-across KPI row.
    const vLen = String(value).length;
    const dFont = big ? (vLen > 9 ? 22 : vLen > 7 ? 25 : 27) : (vLen > 9 ? 20 : vLen > 7 ? 22 : 25);
    const mFont = vLen > 9 ? 13 : vLen > 7 ? 14 : 16;

    if (isDesktop) {
        return (
            <div style={{
                flex: "1 1 130px", minWidth: 130, boxSizing: "border-box", position: "relative", overflow: "hidden",
                background: T.surface, borderRadius: 14, padding: "16px 14px",
                border: `1px solid ${big ? `${T.accent}44` : T.cardBorder}`, boxShadow: T.cardShadow,
            }}>
                {big && <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: T.accentSoft, filter: "blur(20px)" }} />}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 10, position: "relative" }}>
                    <span style={{ fontSize: 11, fontWeight: 500, color: T.t2, textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
                    <InfoTip text={kpi.info} metric={kpi.key} />
                </div>
                <div style={{ fontSize: dFont, fontWeight: 700, color: T.t1, letterSpacing: -0.5, position: "relative", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
                {secondary && <div style={{ fontSize: 11.5, fontWeight: 500, color: T.t3, marginTop: 3 }}>{secondary}</div>}
                <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor, marginTop: 4, position: "relative", minHeight: 16 }}>
                    {pointInTime ? <span style={{ fontWeight: 400, color: T.t3 }}>{tertiary}</span>
                        : showDelta && hasDelta ? <>{deltaText}<span style={{ fontWeight: 400, color: T.t3, marginLeft: 4 }}>{priorLabel}</span></>
                            : <span style={{ fontWeight: 400, color: T.t3 }}>{tertiary ?? ""}</span>}
                </div>
            </div>
        );
    }
    return (
        <div style={{ position: "relative", background: T.surface, borderRadius: 12, padding: "12px 10px", border: `1px solid ${big ? `${T.accent}44` : T.cardBorder}`, boxShadow: T.shadow, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 8, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mobileLabel}</span>
                <InfoTip text={kpi.info} metric={kpi.key} />
            </div>
            <div style={{ fontSize: mFont, fontWeight: 700, color: T.t1, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
            {secondary && <div style={{ fontSize: 8.5, color: T.t3, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{secondary}</div>}
            {pointInTime ? <div style={{ fontSize: 8, color: T.t3, marginTop: 2 }}>{tertiary}</div>
                : showDelta && hasDelta && <div style={{ fontSize: 9, fontWeight: 600, color: deltaColor, marginTop: 2 }}>{deltaText}</div>}
        </div>
    );
}

/* ─── Section card wrapper ───────────────────────────────────────────────── */
function SectionCard({ title, subtitle, info, right, children, id }: any) {
    const isDesktop = useIsDesktop();
    return (
        <div id={id} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: isDesktop ? 22 : 14, boxShadow: T.cardShadow }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontSize: isDesktop ? 15 : 13, fontWeight: 700, color: T.t1, display: "flex", alignItems: "center" }}>
                        {title}{info && <InfoTip text={info} metric={id || title} />}
                    </div>
                    {subtitle && <div style={{ fontSize: isDesktop ? 11 : 10, color: T.t3, marginTop: 2 }}>{subtitle}</div>}
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

/* ─── Chart tooltip ──────────────────────────────────────────────────────── */
function ChartTip({ active, payload, label, sym }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "8px 10px", boxShadow: T.shadow, fontFamily: T.font }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.t1, marginBottom: 4 }}>{label}</div>
            {payload.map((p: any) => (
                <div key={p.dataKey} style={{ fontSize: 11, color: T.t2, display: "flex", gap: 8, justifyContent: "space-between" }}>
                    <span style={{ color: p.color }}>● {p.name}</span>
                    <span style={{ fontWeight: 600, color: T.t1 }}>{p.dataKey === "cumulative" || p.name?.includes("$") ? (sym && p.name?.includes("$") ? fmtMoney(p.value, sym) : fmtNumber(p.value)) : fmtNumber(p.value)}</span>
                </div>
            ))}
        </div>
    );
}

const EmptyChart = ({ msg }: { msg: string }) => (
    <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: T.t3, fontSize: 13 }}>{msg}</div>
);

/* ─── Points Flow Trend (LOY-RPT-P-3.1) ──────────────────────────────────── */
function PointsFlowChart({ flowSeries, bucketNote, isEmpty }: { flowSeries: { label: string; issued: number; redeemed: number }[]; bucketNote: string; isEmpty: boolean }) {
    // Header (id / subtitle / info) is identical whether or not there is data — only the
    // body swaps to the empty message. Same shape as MembersGrowthChart below; the empty
    // state must never restate the empty message as the subtitle.
    return (
        <SectionCard id="loy-points-flow" title="Points Flow Trend" subtitle={`Issued vs Redeemed · ${bucketNote}`}
            info="Points issued versus points redeemed over time. Rising issuance above redemption means liability is accumulating; redemption catching up means points are converting to rewards.">
            {isEmpty ? <EmptyChart msg="No loyalty activity for the selected period." /> : (
                <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={flowSeries} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
                        <defs>
                            <linearGradient id="loyIssued" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={T.green} stopOpacity={0.28} />
                                <stop offset="100%" stopColor={T.green} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: T.t3 }} tickLine={false} axisLine={{ stroke: T.divider }} minTickGap={16} />
                        <YAxis tick={{ fontSize: 10, fill: T.t3 }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)} />
                        <Tooltip content={<ChartTip />} />
                        <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                        <Area type="monotone" dataKey="issued" name="Points Issued" stroke={T.green} strokeWidth={2} fill="url(#loyIssued)" />
                        <Line type="monotone" dataKey="redeemed" name="Points Redeemed" stroke={T.accent} strokeWidth={2} dot={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            )}
        </SectionCard>
    );
}

/* ─── Transaction-Type Breakdown (LOY-RPT-P-3.2) ─────────────────────────── */
function TxnBreakdownChart({ breakdown, isEmpty }: { breakdown: { type: string; points: number; color: string }[]; isEmpty: boolean }) {
    // Header stays identical in the empty state — see PointsFlowChart above.
    return (
        <SectionCard id="loy-txn-breakdown" title="Transaction Breakdown" subtitle="Point volume by transaction type"
            info="How points activity splits across earn, redeem, adjust, and expire, so you can read the composition of point movement at a glance.">
            {isEmpty || breakdown.length === 0 ? <EmptyChart msg="No point movement for the selected period." /> : (
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={breakdown} layout="vertical" margin={{ top: 6, right: 56, left: 4, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.divider} horizontal={false} />
                        {/* domain headroom keeps the value label (rendered past the bar's end) from
                            being clipped when the largest bar would otherwise span the full plot width */}
                        <XAxis type="number" domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.15)]} tick={{ fontSize: 10, fill: T.t3 }} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)} />
                        <YAxis type="category" dataKey="type" tick={{ fontSize: 11, fill: T.t2 }} tickLine={false} axisLine={false} width={64} />
                        <Tooltip content={<ChartTip />} cursor={{ fill: T.hover }} />
                        <Bar dataKey="points" name="Points" radius={[0, 5, 5, 0]} label={{ position: "right", fontSize: 10, fill: T.t2, formatter: (v: number) => fmtNumber(v) }}>
                            {breakdown.map((b) => <Cell key={b.type} fill={b.color} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </SectionCard>
    );
}

/* ─── Members Growth (LOY-RPT-P-3.3) ─────────────────────────────────────── */
function MembersGrowthChart({ growthSeries, bucketNote }: { growthSeries: { label: string; newMembers: number; cumulative: number }[]; bucketNote: string }) {
    return (
        <SectionCard id="loy-members-growth" title="Members Growth" subtitle={`New enrollments & cumulative membership · ${bucketNote}`}
            info="New enrollments per interval (bars) and cumulative membership (line) over the selected range, for the selected outlet.">
            {growthSeries.length === 0 ? <EmptyChart msg="No enrollment data for the selected period." /> : (
                <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={growthSeries} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: T.t3 }} tickLine={false} axisLine={{ stroke: T.divider }} minTickGap={16} />
                        {/* allowDecimals=false: newMembers is a whole-number count, so fractional
                            auto-ticks (0.75, 1.5, 2.25 ...) on a small range not only read oddly
                            but ran long enough to get clipped against the card edge at width=30 */}
                        <YAxis yAxisId="l" allowDecimals={false} tick={{ fontSize: 10, fill: T.t3 }} tickLine={false} axisLine={false} width={34} />
                        <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: T.t3 }} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`)} />
                        <Tooltip content={<ChartTip />} cursor={{ fill: T.hover }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                        <Bar yAxisId="l" dataKey="newMembers" name="New Members" fill={T.blue} radius={[4, 4, 0, 0]} maxBarSize={26} />
                        <Line yAxisId="r" type="monotone" dataKey="cumulative" name="Cumulative" stroke={T.purple} strokeWidth={2} dot={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            )}
        </SectionCard>
    );
}

/* ─── Loyalty Value section (LOY-RPT-P-4.1 / 4.2) ────────────────────────── */
function LoyaltyValueSection({ sym, showDelta, priorLabel, attributedRevenue, attributedRevenueDelta, memberOrderCount, loyaltyAov, nonAov, lift }: {
    sym: string; showDelta: boolean; priorLabel: string;
    attributedRevenue: number; attributedRevenueDelta: number | null; memberOrderCount: number;
    loyaltyAov: number | null; nonAov: number | null; lift: number | null;
}) {
    const isDesktop = useIsDesktop();
    const safe = Number(attributedRevenueDelta) || 0;
    const deltaColor = safe === 0 ? T.t3 : safe > 0 ? T.green : T.red;
    const liftColor = lift == null ? T.t3 : lift >= 0 ? T.green : T.red;
    const maxAov = Math.max(loyaltyAov ?? 0, nonAov ?? 0, 1);
    const AovBar = ({ label, aov, color }: { label: string; aov: number | null; color: string }) => (
        <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: T.t2 }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{aov == null ? "—" : fmtMoney(aov, sym)}</span>
            </div>
            <div style={{ height: 8, background: T.hover, borderRadius: 4, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${((aov ?? 0) / maxAov) * 100}%`, background: color, borderRadius: 4 }} />
            </div>
        </div>
    );
    return (
        <div style={{ display: isDesktop ? "grid" : "flex", gridTemplateColumns: "1fr 1fr", flexDirection: "column", gap: isDesktop ? 16 : 12 }}>
            <SectionCard id="loy-attributed-revenue" title="Loyalty-Attributed Revenue"
                subtitle={`${fmtNumber(memberOrderCount)} member orders in period`}
                info="How much money your loyalty members actually brought in this period. We take what they paid, subtract any discounts and refunds, and leave tax out — so this is the real sales kept from member orders. An order counts if it's tied to a member, even if no points were earned or redeemed on it.">
                <div style={{ fontSize: 30, fontWeight: 700, color: T.t1, letterSpacing: -0.5 }}>{fmtMoney(attributedRevenue, sym)}</div>
                {showDelta && attributedRevenueDelta != null && (
                    <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor, marginTop: 4 }}>
                        {safe > 0 ? "↑" : safe < 0 ? "↓" : ""} {Math.abs(safe).toFixed(1)}%
                        <span style={{ fontWeight: 400, color: T.t3, marginLeft: 4 }}>{priorLabel}</span>
                    </div>
                )}
            </SectionCard>
            <SectionCard id="loy-aov-lift" title="Loyalty vs Non-Loyalty AOV"
                subtitle="Average order value comparison"
                info="Compares the average order size of loyalty members versus non-members. Both are measured the same way — what was paid per order, minus discounts, with tax left out — so it's a fair side-by-side. A positive lift means members tend to spend more per order (it's a strong signal, not absolute proof, that the program drives bigger baskets).">
                <AovBar label="Loyalty members" aov={loyaltyAov} color={T.accent} />
                <AovBar label="Non-members" aov={nonAov} color={`${T.t3}`} />
                <div style={{ marginTop: 8, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: T.t2 }}>AOV Lift</span>
                    <span style={{ fontSize: 18, fontWeight: 700, color: liftColor }}>{lift == null ? "—" : `${lift >= 0 ? "+" : ""}${lift.toFixed(1)}%`}</span>
                </div>
            </SectionCard>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   GENERIC TABLE  (sortable / searchable / paginated / exportable)
   ═══════════════════════════════════════════════════════════════════════════ */
interface Col {
    key: string; label: string; align?: "left" | "right"; sortable?: boolean;
    value?: (r: any) => number | string; render: (r: any) => React.ReactNode; info?: string;
    // Optional layout overrides for columns that need tighter/looser spacing than the
    // table's default auto layout gives them (e.g. a short numeric column that
    // table-layout:auto stretches with dead space around a right-aligned value).
    width?: number; padLeft?: number;
}
interface ServerPagination {
    page: number; pageSize: number; totalElements: number; loading?: boolean;
    onPageChange: (page: number) => void; onPageSizeChange: (size: number) => void;
}
interface ServerSort { sort: { key: string; dir: "desc" | "asc" } | null; onChange: (sort: { key: string; dir: "desc" | "asc" } | null) => void; }
interface ServerSearch { value: string; onChange: (v: string) => void; }

// Section-level table shimmer for server-paginated tables (page/sort/search change) —
// mirrors EventReport.tsx's TableShimmer: full row/card bones replace the stale page
// while the next one loads, rather than dimming the old rows.
function LoyTableShimmer({ cols = 5, rows = 6, mobile }: { cols?: number; rows?: number; mobile?: boolean }) {
    if (mobile) {
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Array.from({ length: rows }).map((_, i) => (
                    <div key={i} style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                            <Bone w={120} h={13} r={4} />
                            <Bone w={50} h={13} r={4} />
                        </div>
                        <Bone w={null} h={10} r={4} mb={4} />
                        <Bone w={"60%" as any} h={10} r={4} />
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div style={{ padding: "4px 0" }}>
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "10px 10px", borderBottom: `1px solid ${T.divider}` }}>
                    {Array.from({ length: cols }).map((_, j) => (
                        <Bone key={j} w={j === 0 ? 140 : undefined} h={13} r={4} style={{ flex: j === 0 ? undefined : 1 }} />
                    ))}
                </div>
            ))}
        </div>
    );
}

function DataTableCard({
    id, title, subtitle, columns, rows, defaultCompare, gaTable, searchPlaceholder,
    searchValue, mobileCard, onRowClick, exportConfig, extraHeader, emptyMsg,
    serverPagination, serverSort, serverSearch, pageSizeOptions,
}: any & { serverPagination?: ServerPagination; serverSort?: ServerSort; serverSearch?: ServerSearch; pageSizeOptions?: number[] }) {
    const isDesktop = useIsDesktop();
    const isServer: boolean = !!serverPagination;
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState<{ key: string; dir: "desc" | "asc" } | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [pageSizeMenuOpen, setPageSizeMenuOpen] = useState(false);

    const searchDisplay: string = serverSearch ? serverSearch.value : search;
    const sortDisplay: { key: string; dir: "desc" | "asc" } | null = serverSort ? serverSort.sort : sort;

    const filtered = useMemo(() => {
        if (isServer) return rows;
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r: any) => String(searchValue ? searchValue(r) : "").toLowerCase().includes(q));
    }, [rows, search, searchValue, isServer]);

    const sorted = useMemo(() => {
        if (isServer) return filtered;
        const arr = [...filtered];
        if (sort) {
            const col = (columns as Col[]).find((c) => c.key === sort.key);
            if (col?.value) {
                arr.sort((a, b) => {
                    const av = col.value!(a), bv = col.value!(b);
                    if (av < bv) return sort.dir === "asc" ? -1 : 1;
                    if (av > bv) return sort.dir === "asc" ? 1 : -1;
                    return 0;
                });
                return arr;
            }
        }
        if (defaultCompare) arr.sort(defaultCompare);
        return arr;
    }, [filtered, sort, columns, defaultCompare, isServer]);

    const allMode = !isServer && pageSize < 0;
    const total = isServer ? serverPagination.totalElements : sorted.length;
    const effectivePageSize = isServer ? serverPagination.pageSize : pageSize;
    const totalPages = allMode ? 1 : Math.max(1, Math.ceil(total / effectivePageSize));
    const safePage = isServer ? serverPagination.page : Math.min(page, totalPages - 1);
    const startIdx = total === 0 ? 0 : allMode ? 1 : safePage * effectivePageSize + 1;
    const endIdx = allMode ? total : Math.min((safePage + 1) * effectivePageSize, total);
    const visible = isServer ? sorted : allMode ? sorted : sorted.slice(safePage * effectivePageSize, (safePage + 1) * effectivePageSize);

    useEffect(() => { if (!isServer) setPage(0); }, [search, pageSize, rows, isServer]);

    const clickSort = (col: Col) => {
        if (col.sortable === false || !col.value) return;
        if (isServer) {
            if (!serverSort) return;
            const prev = serverSort.sort;
            const dir = !prev || prev.key !== col.key ? "desc" : prev.dir === "desc" ? "asc" : null as any;
            track("loyalty_table_sort", { table: gaTable, sort_column: col.key, sort_direction: dir || "reset" });
            serverSort.onChange(dir ? { key: col.key, dir } : null);
            return;
        }
        setSort((prev) => {
            const dir = !prev || prev.key !== col.key ? "desc" : prev.dir === "desc" ? "asc" : null as any;
            track("loyalty_table_sort", { table: gaTable, sort_column: col.key, sort_direction: dir || "reset" });
            return dir ? { key: col.key, dir } : null;
        });
    };

    const SearchBox = (
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: isDesktop ? "6px 10px" : "5px 8px", borderRadius: 8, background: T.hover, border: `1px solid ${T.cardBorder}`, minWidth: isDesktop ? 190 : 140, flex: isDesktop ? undefined : 1 }}>
            <span className="material-symbols-rounded" style={{ fontSize: isDesktop ? 16 : 14, color: T.t3 }}>search</span>
            <input className="rpt-filter-search-input" style={{ fontSize: isDesktop ? 11 : 10 }} value={searchDisplay} onChange={(e) => (serverSearch ? serverSearch.onChange(e.target.value) : setSearch(e.target.value))} placeholder={searchPlaceholder || "Search by name"} />
        </div>
    );

    const pagBtn = (disabled: boolean) => ({
        padding: isDesktop ? "4px 10px" : "3px 8px", borderRadius: 6, border: `1px solid ${T.cardBorder}`,
        background: disabled ? T.hover : T.surface, color: disabled ? T.t3 : T.t1,
        fontSize: isDesktop ? 11 : 10, fontWeight: 600 as const, cursor: disabled ? "default" : "pointer", fontFamily: T.font,
    });
    const goPrev = () => (isServer ? serverPagination.onPageChange(Math.max(0, safePage - 1)) : setPage((p) => Math.max(0, p - 1)));
    const goNext = () => (isServer ? serverPagination.onPageChange(Math.min(totalPages - 1, safePage + 1)) : setPage((p) => Math.min(totalPages - 1, p + 1)));
    const changePageSize = (size: number) => (isServer ? serverPagination.onPageSizeChange(size) : setPageSize(size));
    const pageSizeChoices = pageSizeOptions ?? (isServer ? [10, 25, 50] : [5, 10, 20]);
    const showSizeSelector = total > 10;
    const pageSizeFontSize = isDesktop ? 11 : 10;
    const PageSizeSelector = showSizeSelector ? (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: pageSizeFontSize, color: T.t3, fontFamily: T.font }}>
            Rows per page:
            <div style={{ position: "relative" }}>
                <button
                    type="button"
                    onClick={() => setPageSizeMenuOpen((o) => !o)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: pageSizeFontSize, fontWeight: 600, color: T.t1, fontFamily: T.font, padding: isDesktop ? "3px 6px" : "2px 4px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer", lineHeight: 1 }}
                >
                    {allMode ? "All" : effectivePageSize}
                    <span className="material-symbols-rounded" style={{ fontSize: pageSizeFontSize + 2, lineHeight: 1, color: T.t3 }}>expand_more</span>
                </button>
                {pageSizeMenuOpen && (
                    <>
                        <div onClick={() => setPageSizeMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 500 }} />
                        <div style={{ position: "absolute", bottom: "calc(100% + 4px)", left: 0, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 501, overflow: "hidden", minWidth: "100%" }}>
                            {pageSizeChoices.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => { changePageSize(s); setPageSizeMenuOpen(false); }}
                                    style={{ display: "block", width: "100%", textAlign: "center", fontSize: pageSizeFontSize, fontWeight: !allMode && s === effectivePageSize ? 700 : 500, color: !allMode && s === effectivePageSize ? T.accent : T.t1, fontFamily: T.font, padding: "7px 14px", border: "none", background: !allMode && s === effectivePageSize ? `${T.accent}12` : "transparent", cursor: "pointer" }}
                                >
                                    {s}
                                </button>
                            ))}
                            {!isServer && (
                                <button
                                    type="button"
                                    onClick={() => { changePageSize(-1); setPageSizeMenuOpen(false); }}
                                    style={{ display: "block", width: "100%", textAlign: "center", fontSize: pageSizeFontSize, fontWeight: allMode ? 700 : 500, color: allMode ? T.accent : T.t1, fontFamily: T.font, padding: "7px 14px", border: "none", background: allMode ? `${T.accent}12` : "transparent", cursor: "pointer" }}
                                >
                                    All
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    ) : null;
    const Pager = total > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
            <div style={{ fontSize: isDesktop ? 11 : 10, color: T.t3 }}>Showing {startIdx}–{endIdx} of {total}</div>
            {(showSizeSelector || (!allMode && totalPages > 1)) && (
                <div style={{ display: "flex", alignItems: "center", gap: isDesktop ? 12 : 8, flexWrap: "wrap" }}>
                    {PageSizeSelector}
                    {!allMode && totalPages > 1 && (
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <button type="button" onClick={goPrev} disabled={safePage === 0 || (isServer && serverPagination.loading)} style={pagBtn(safePage === 0)}>← Prev</button>
                            <span style={{ fontSize: isDesktop ? 11 : 10, color: T.t2, padding: "4px 6px" }}>{safePage + 1} / {totalPages}</span>
                            <button type="button" onClick={goNext} disabled={safePage >= totalPages - 1 || (isServer && serverPagination.loading)} style={pagBtn(safePage >= totalPages - 1)}>Next →</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // Hide search + export once the table is truly empty (no rows, no active search),
    // rather than just disabling export — an active search that happens to match zero
    // rows keeps the search box so the user can still edit/clear it (LOY-RPT empty-state rule).
    const hasSearchText = !!searchDisplay.trim();
    const showSearchBox = total > 0 || hasSearchText;
    const showExportButton = total > 0;
    const HeaderRight = isDesktop && extraHeader ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "space-between", width: "100%" }}>
            {extraHeader}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {showSearchBox && SearchBox}
                {exportConfig && showExportButton && <ReportSectionExportButton title={exportConfig.title} filename={exportConfig.filename} buildExport={exportConfig.buildExport} outletScoped={exportConfig.outletScoped} dateRangeLabel={exportConfig.dateRangeLabel} />}
            </div>
        </div>
    ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: isDesktop ? "flex-end" : "flex-start", width: isDesktop ? undefined : "100%" }}>
            {extraHeader}
            {showSearchBox && SearchBox}
            {exportConfig && showExportButton && <ReportSectionExportButton title={exportConfig.title} filename={exportConfig.filename} buildExport={exportConfig.buildExport} outletScoped={exportConfig.outletScoped} dateRangeLabel={exportConfig.dateRangeLabel} />}
        </div>
    );

    const isPaginationLoading = isServer && !!serverPagination.loading;

    return (
        <SectionCard id={id} title={title} subtitle={subtitle} right={HeaderRight}>
            {isPaginationLoading ? (
                <LoyTableShimmer cols={(columns as Col[]).length} rows={Math.min(effectivePageSize, 8)} mobile={!isDesktop} />
            ) : total === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: T.t3, fontSize: 13 }}>
                    {searchDisplay ? `No results match "${searchDisplay.trim()}"` : (emptyMsg || "No records for the selected period.")}
                </div>
            ) : isDesktop ? (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font }}>
                        <thead>
                            <tr>
                                {(columns as Col[]).map((c) => {
                                    const active = sortDisplay?.key === c.key;
                                    return (
                                        <th key={c.key} style={{ textAlign: c.align || "left", fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.4, padding: `0 10px 10px ${c.padLeft ?? 10}px`, width: c.width, borderBottom: `1px solid ${T.divider}`, whiteSpace: "nowrap" }}>
                                            <span onClick={() => clickSort(c)} style={{ cursor: c.sortable === false || !c.value ? "default" : "pointer", userSelect: "none", display: "inline-flex", alignItems: "center", gap: c.align === "right" ? 0 : 2 }}>
                                                {c.label}
                                                {active && <span style={{ marginLeft: 3 }}>{sortDisplay!.dir === "desc" ? "▾" : "▴"}</span>}
                                                {c.info && <InfoTip text={c.info} metric={c.key} />}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((r: any, i: number) => (
                                <tr key={r.id || i} onClick={onRowClick ? () => onRowClick(r) : undefined}
                                    style={{ borderBottom: `1px solid ${T.divider}`, cursor: onRowClick ? "pointer" : "default" }}
                                    onMouseEnter={onRowClick ? (e) => (e.currentTarget.style.background = T.hover) : undefined}
                                    onMouseLeave={onRowClick ? (e) => (e.currentTarget.style.background = "transparent") : undefined}>
                                    {(columns as Col[]).map((c) => (
                                        <td key={c.key} style={{ textAlign: c.align || "left", fontSize: 12, color: T.t1, padding: `10px 10px 10px ${c.padLeft ?? 10}px`, width: c.width, whiteSpace: "nowrap" }}>{c.render(r)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {visible.map((r: any, i: number) => <div key={r.id || i} onClick={onRowClick ? () => onRowClick(r) : undefined}>{mobileCard(r)}</div>)}
                </div>
            )}
            {Pager}
        </SectionCard>
    );
}

/* ─── Top Members table (LOY-RPT-P-5.1) — server-paginated ──────────────── */
type TopMembersState = ReturnType<typeof useTopMembers>;
function TopMembersTable({ topMembers, sym, onOpenMember, exportFetch }: {
    topMembers: TopMembersState; sym: string; onOpenMember: (m: LoyMember) => void;
    exportFetch: (locationId?: string) => Promise<LoyMember[]>;
}) {
    // Only Balance sorting is confirmed supported by the API; other columns stay display-only
    // to avoid sorting just the currently-loaded page in a way that misrepresents the full list.
    const columns: Col[] = [
        { key: "name", label: "Member", sortable: false, render: (r: LoyMember) => (<div><div style={{ color: T.accent, fontWeight: 600 }}>{r.name}</div><div style={{ fontSize: 10, color: T.t3 }}>{r.contact}</div></div>) },
        { key: "channel", label: "Channel", sortable: false, render: (r: LoyMember) => <span style={{ fontSize: 11, color: T.t2 }}>{r.channel}</span> },
        { key: "balance", label: "Current Balance", align: "right", value: (r) => r.balance, render: (r: LoyMember) => <strong>{fmtNumber(r.balance)}</strong>, info: "Current unredeemed points balance for the member, at the selected outlet." },
        { key: "earned", label: "Lifetime Earned", align: "right", sortable: false, render: (r: LoyMember) => fmtNumber(r.lifetimeEarned) },
        { key: "redeemed", label: "Lifetime Redeemed", align: "right", sortable: false, render: (r: LoyMember) => fmtNumber(r.lifetimeRedeemed) },
        { key: "orders", label: "Orders", align: "right", sortable: false, render: (r: LoyMember) => `${r.orders}` },
        { key: "last", label: "Last Activity", align: "right", sortable: false, render: (r: LoyMember) => fmtDayLabel(r.lastActivity, true) },
    ];
    const mobileCard = (r: LoyMember) => (
        <div style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <div><div style={{ color: T.accent, fontWeight: 600, fontSize: 13 }}>{r.name}</div><div style={{ fontSize: 10, color: T.t3 }}>{r.contact} · {r.channel}</div></div>
                <span style={{ fontWeight: 700, fontSize: 14, color: T.t1 }}>{fmtNumber(r.balance)}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, fontSize: 11 }}>
                <MiniStat label="Earned" value={fmtNumber(r.lifetimeEarned)} />
                <MiniStat label="Redeemed" value={fmtNumber(r.lifetimeRedeemed)} />
                <MiniStat label="Orders" value={`${r.orders}`} />
            </div>
            <div style={{ fontSize: 10, color: T.t3, marginTop: 8 }}>Last activity {fmtDayLabel(r.lastActivity, true)}</div>
        </div>
    );
    return (
        <DataTableCard
            id="loy-top-members" title="Top Members"
            subtitle="Ranked by current balance"
            columns={columns} rows={topMembers.members} searchPlaceholder="Search member name or phone number"
            gaTable="top_members" mobileCard={mobileCard}
            onRowClick={onOpenMember}
            emptyMsg="No members enrolled yet."
            pageSizeOptions={[10, 20, 30]}
            serverPagination={{
                page: topMembers.page, pageSize: topMembers.pageSize, totalElements: topMembers.totalElements,
                loading: topMembers.loading, onPageChange: topMembers.setPage, onPageSizeChange: topMembers.setPageSize,
            }}
            serverSort={{
                sort: { key: "balance", dir: topMembers.sortDir },
                onChange: (s: { key: string; dir: "desc" | "asc" } | null) => topMembers.setSortDir(s ? s.dir : "desc"),
            }}
            serverSearch={{ value: topMembers.search, onChange: topMembers.setSearch }}
            exportConfig={{
                title: "Top Members", filename: `Loyalty_TopMembers_${isoDate(new Date())}`,
                buildExport: async (_format, opts) => {
                    const all = await exportFetch(opts?.locationId);
                    return {
                        headers: ["Member", "Channel", "Current Balance", "Lifetime Earned", "Lifetime Redeemed", "Orders", "Last Activity"],
                        rows: all.map((m) => [m.name, m.channel, roundNum(m.balance), roundNum(m.lifetimeEarned), roundNum(m.lifetimeRedeemed), m.orders, m.lastActivity]),
                    };
                },
            }}
        />
    );
}

/* ─── Loyalty Activity Log (LOY-RPT-P-5.2) ───────────────────────────────── */
const TYPE_COLORS: Record<TxnType, string> = { Earn: T.green, Redeem: T.accent, AdjustCredit: T.blue, AdjustDebit: T.blue, Expire: T.orange, Refund: T.teal, Reversal: T.purple };
// Credit vs debit adjustments share the raw "adjust" category but move points in opposite
// directions, so the badge/filter/export text spells out which one rather than a single
// ambiguous "Adjust" — matches TxnType 1:1 so every place rendering a.type can use this.
const TYPE_LABELS: Record<TxnType, string> = { Earn: "Earn", Redeem: "Redeem", AdjustCredit: "Adjust Credited", AdjustDebit: "Adjust Debited", Expire: "Expire", Refund: "Refund", Reversal: "Reversal" };
const TYPE_FILTERS: (TxnType | "All")[] = ["All", "Earn", "Redeem", "AdjustCredit", "AdjustDebit", "Expire", "Refund", "Reversal"];
type ActivityLogState = ReturnType<typeof useActivityLog>;
function ActivityLog({ activityLog, onOpenMember, exportFetch }: {
    activityLog: ActivityLogState; onOpenMember: (memberId: string, name: string) => void;
    exportFetch: (locationId?: string) => Promise<LoyActivity[]>;
}) {
    const pointsCell = (a: LoyActivity) => <span style={{ color: a.points >= 0 ? T.green : T.red, fontWeight: 600 }}>{a.points >= 0 ? "+" : ""}{fmtNumber(a.points)}</span>;
    const typeBadge = (a: LoyActivity) => <span style={{ fontSize: 10, fontWeight: 600, color: TYPE_COLORS[a.type], background: `${TYPE_COLORS[a.type]}18`, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>{TYPE_LABELS[a.type]}</span>;
    // Date/Time and Points are server-sortable per LOY-RPT-P-5.2 (sortBy=dateTime|points,
    // unconfirmed against the backend — see useActivityLog). Every other column has no
    // documented sortBy value, so sorting the currently-loaded page alone would misrepresent
    // the full log and stays display-only.
    const columns: Col[] = [
        // key matches the API's sortBy value directly (dateTime/points) so serverSort
        // needs no translation layer between the column and the query param.
        { key: "dateTime", label: "Date / Time", value: (r: LoyActivity) => r.dateTime, render: (r: LoyActivity) => <span style={{ color: T.t2 }}>{fmtDateTime(r.dateTime)}</span> },
        { key: "member", label: "Member", sortable: false, render: (r: LoyActivity) => <span style={{ color: T.accent, fontWeight: 600 }}>{r.member}</span> },
        { key: "type", label: "Type", sortable: false, render: typeBadge },
        { key: "points", label: "Points", align: "right", width: 70, value: (r: LoyActivity) => r.points, render: pointsCell },
        { key: "bal", label: "Balance After", align: "right", sortable: false, width: 100, render: (r: LoyActivity) => fmtNumber(r.balanceAfter) },
        { key: "loc", label: "Location", sortable: false, padLeft: 36, render: (r: LoyActivity) => <span style={{ color: T.t2 }}>{r.location}</span> },
        { key: "detail", label: "Detail", sortable: false, render: (r: LoyActivity) => <span style={{ color: T.t3 }}>{r.detail}</span> },
    ];
    const mobileCard = (a: LoyActivity) => (
        <div style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ color: T.accent, fontWeight: 600, fontSize: 12 }}>{a.member}</span>
                {typeBadge(a)}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontSize: 10, color: T.t3 }}>{fmtDateTime(a.dateTime)}<Dot />{a.location}</span>
                {pointsCell(a)}
            </div>
            <div style={{ fontSize: 10, color: T.t3, marginTop: 4 }}>{a.detail}<Dot />bal {fmtNumber(a.balanceAfter)}</div>
        </div>
    );
    const TypeFilter = (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {TYPE_FILTERS.map((t) => {
                const active = activityLog.typeFilter === t;
                return (
                    <button key={t} type="button" onClick={() => { activityLog.setTypeFilter(t); track("loyalty_filter_applied", { filter_type: "txn_type", filter_value: t }); }}
                        style={{ padding: "4px 10px", borderRadius: 20, border: `1px solid ${active ? T.accent : T.cardBorder}`, background: active ? T.accentSoft : T.surface, color: active ? T.accent : T.t2, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: T.font, whiteSpace: "nowrap" }}>
                        {t === "All" ? "All" : TYPE_LABELS[t]}
                    </button>
                );
            })}
        </div>
    );
    return (
        <DataTableCard
            id="loy-activity-log" title="Loyalty Activity Log"
            subtitle="Transaction-level point activity for the selected period"
            columns={columns} rows={activityLog.rows} searchPlaceholder="Search member name or phone number"
            gaTable="activity_log" mobileCard={mobileCard}
            onRowClick={(a: LoyActivity) => onOpenMember(a.memberId, a.member)}
            extraHeader={TypeFilter}
            emptyMsg="No activity for the selected period."
            pageSizeOptions={[10, 20, 30]}
            serverPagination={{
                page: activityLog.page, pageSize: activityLog.pageSize, totalElements: activityLog.totalElements,
                loading: activityLog.loading, onPageChange: activityLog.setPage, onPageSizeChange: activityLog.setPageSize,
            }}
            serverSort={{
                sort: activityLog.sortBy ? { key: activityLog.sortBy, dir: activityLog.sortDir } : null,
                onChange: (s: { key: string; dir: "desc" | "asc" } | null) => {
                    // Reset (3rd click) clears sortBy — falls back to the spec's default
                    // ordering (Date/Time desc) instead of forcing a param on the request.
                    if (!s) { activityLog.setSortBy(null); activityLog.setSortDir("desc"); return; }
                    activityLog.setSortBy(s.key as "dateTime" | "points"); activityLog.setSortDir(s.dir);
                },
            }}
            serverSearch={{ value: activityLog.search, onChange: activityLog.setSearch }}
            exportConfig={{
                title: "Loyalty Activity Log", filename: `Loyalty_ActivityLog_${isoDate(new Date())}`,
                buildExport: async (_format, opts) => {
                    const all = await exportFetch(opts?.locationId);
                    return {
                        headers: ["Date/Time", "Member", "Type", "Points", "Balance After", "Location", "Detail"],
                        rows: all.map((a) => [fmtDateTime(a.dateTime), a.member, TYPE_LABELS[a.type], roundNum(a.points), roundNum(a.balanceAfter), a.location, a.detail]),
                    };
                },
            }}
        />
    );
}

// Mobile card detail lines pack several fields on one row separated by "·" — at the
// cards' 10px text size a plain inline "·" all but disappears. Bumping just the dot's
// size/weight keeps the fields visually separated without enlarging the surrounding text.
const Dot = () => <span style={{ margin: "0 6px", fontSize: 20, fontWeight: 700, color: T.t3, lineHeight: 1, verticalAlign: "middle" }}>·</span>;

function MiniStat({ label, value, color }: any) {
    return (
        <div>
            <div style={{ color: T.t3, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
            <div style={{ color: color || T.t1, fontWeight: 600, fontSize: 11, marginTop: 1 }}>{value}</div>
        </div>
    );
}

/* ─── Outlet filter dropdown (real branch list from merchant location API) ── */
function OutletFilter({ value, onChange, branches, loading, compact }: { value: string | null; onChange: (id: string) => void; branches: OutletBranch[]; loading: boolean; compact?: boolean }) {
    const { auth } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);
    const selected = value === ALL_OUTLETS
        ? "All outlets"
        : branches.find((o) => o.id === value)?.name ?? (loading ? "Loading…" : "Select outlet");

    const userRole = (auth as any)?.role ?? (auth as any)?.designation ?? "";
    const isSuperUser = SUPER_ROLES.includes(userRole);

    // Non-super users cannot switch outlets — same rule as OutletDropdown.tsx's
    // sidebar/topbar switcher — so this renders as a read-only label instead.
    if (!isSuperUser) {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.surface, color: T.t2, fontSize: 12, fontWeight: 500, fontFamily: T.font, width: compact ? "100%" : undefined, boxSizing: "border-box" }}>
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>storefront</span>
                <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected}</span>
            </div>
        );
    }

    return (
        <div ref={ref} style={{ position: "relative", width: compact ? "100%" : undefined }}>
            <button type="button" onClick={() => setOpen((o) => !o)}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, border: `1px solid ${value ? `${T.accent}55` : T.cardBorder}`, background: value ? T.accentSoft : T.surface, color: value ? T.accent : T.t2, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: T.font, width: compact ? "100%" : undefined, justifyContent: compact ? "space-between" : undefined }}>
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>storefront</span>
                <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected}</span>
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>expand_more</span>
            </button>
            {open && (
                <div style={{ position: "absolute", top: "100%", right: compact ? undefined : 0, left: compact ? 0 : undefined, marginTop: 6, width: 220, maxWidth: "90vw", background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 300, overflow: "hidden", maxHeight: 280, overflowY: "auto" }}>
                    {loading && <div style={{ padding: "10px 12px", fontSize: 12, color: T.t3 }}>Loading branches…</div>}
                    {!loading && branches.length === 0 && <div style={{ padding: "10px 12px", fontSize: 12, color: T.t3 }}>No branches found</div>}
                    {/* Only worth offering with more than one branch — same rule
                        as the Owner View's "All stores" entry. */}
                    {!loading && branches.length > 1 && (
                        <div onClick={() => { onChange(ALL_OUTLETS); setOpen(false); track("loyalty_filter_applied", { filter_type: "outlet", filter_value: "All outlets" }); }}
                            style={{ padding: "10px 12px", fontSize: 12, cursor: "pointer", background: value === ALL_OUTLETS ? T.accentSoft : "transparent", color: value === ALL_OUTLETS ? T.accent : T.t1, fontWeight: value === ALL_OUTLETS ? 600 : 400 }}>
                            All outlets
                        </div>
                    )}
                    {!loading && branches.map((o) => {
                        const active = o.id === value;
                        return (
                            <div key={o.id} onClick={() => { onChange(o.id); setOpen(false); track("loyalty_filter_applied", { filter_type: "outlet", filter_value: o.name }); }}
                                style={{ padding: "10px 12px", fontSize: 12, cursor: "pointer", background: active ? T.accentSoft : "transparent", color: active ? T.accent : T.t1, fontWeight: active ? 600 : 400 }}>
                                {o.name}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MEMBER DETAIL DRILL-IN  (in-report preview; deep-links out to Admin Portal)
   ═══════════════════════════════════════════════════════════════════════════ */
function MemberDetail({ member, sym, isDesktop, onOpenAdmin, token }: { member: LoyMember; sym: string; isDesktop: boolean; onOpenAdmin: () => void; token: string | null }) {
    const memberActivity = useMemberActivityLog(member.id, token);
    const memberActivitySearch = memberActivity.search;
    const memberActivityExportFetch = useCallback(
        () => fetchAllMemberActivityLogForExport(member.id, memberActivitySearch, token),
        [member.id, memberActivitySearch, token],
    );
    const { profile } = useMemberProfile(member.id, PROGRAM.dollarValuePerPoint, token);
    // Fall back to the row data passed in from Top Members / Activity Log while the
    // dedicated profile fetch is in flight (or if it comes back empty).
    const displayName = profile?.memberName ?? member.name;
    const displayContact = profile?.maskedContact ?? member.contact;
    const displayChannel = (profile?.enrollmentChannel ?? member.channel ?? "").toString().toUpperCase();
    const displayLastActivity = profile?.lastActivity ?? member.lastActivity;
    const stats = [
        { label: "Current Balance", value: fmtNumber(profile?.currentBalance ?? member.balance), color: T.accent },
        { label: "Lifetime Earned", value: fmtNumber(profile?.lifetimeEarned ?? member.lifetimeEarned) },
        { label: "Lifetime Redeemed", value: fmtNumber(profile?.lifetimeRedeemed ?? member.lifetimeRedeemed) },
        { label: "Orders", value: fmtNumber(profile?.orders ?? member.orders) },
        { label: "Liability Value", value: fmtMoney(profile?.liabilityValue ?? 0, sym) },
    ];
    // Admin Portal deep-link is not wired up yet — hidden until that destination exists.
    // const adminBtn = (
    //     <button type="button" onClick={onOpenAdmin}
    //         style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: `1px solid ${T.accent}`, background: T.accentSoft, color: T.accent, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
    //         <span className="material-symbols-rounded" style={{ fontSize: 15 }}>open_in_new</span>
    //         Open in Admin Portal
    //     </button>
    // );
    const pointsCell = (a: LoyActivity) => <span style={{ color: a.points >= 0 ? T.green : T.red, fontWeight: 600 }}>{a.points >= 0 ? "+" : ""}{fmtNumber(a.points)}</span>;
    const typeBadge = (a: LoyActivity) => <span style={{ fontSize: 10, fontWeight: 600, color: TYPE_COLORS[a.type], background: `${TYPE_COLORS[a.type]}18`, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>{TYPE_LABELS[a.type]}</span>;
    // No sortBy param is documented for this endpoint (same one behind the main Activity
    // Log), so columns stay display-only — sorting a single loaded page would misrepresent
    // the member's full history.
    const columns: Col[] = [
        { key: "dt", label: "Date / Time", sortable: false, render: (r: LoyActivity) => <span style={{ color: T.t2 }}>{fmtDateTime(r.dateTime)}</span> },
        { key: "type", label: "Type", sortable: false, render: typeBadge },
        { key: "points", label: "Points", align: "right", sortable: false, render: pointsCell },
        { key: "bal", label: "Balance After", align: "right", sortable: false, render: (r: LoyActivity) => fmtNumber(r.balanceAfter) },
        { key: "loc", label: "Location", sortable: false, render: (r: LoyActivity) => <span style={{ color: T.t2 }}>{r.location}</span> },
        { key: "detail", label: "Detail", sortable: false, render: (r: LoyActivity) => <span style={{ color: T.t3 }}>{r.detail}</span> },
    ];
    const mobileCard = (a: LoyActivity) => (
        <div style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>{typeBadge(a)}{pointsCell(a)}</div>
            <div style={{ fontSize: 10, color: T.t3 }}>{fmtDateTime(a.dateTime)}<Dot />{a.location}</div>
            <div style={{ fontSize: 10, color: T.t3, marginTop: 4 }}>{a.detail}<Dot />bal {fmtNumber(a.balanceAfter)}</div>
        </div>
    );
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <SectionCard title={displayName} subtitle={`${displayContact} · Enrolled via ${displayChannel} · Last activity ${fmtDayLabel(displayLastActivity)}`}>
                <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(5, 1fr)" : "1fr 1fr 1fr", gap: isDesktop ? 16 : 8 }}>
                    {stats.map((s) => (
                        <div key={s.label} style={{ textAlign: "center", padding: isDesktop ? "16px 12px" : "12px 8px", background: T.hover, borderRadius: 12, border: `1px solid ${T.cardBorder}` }}>
                            <div style={{ fontSize: isDesktop ? 11 : 9, color: T.t3, textTransform: "uppercase", letterSpacing: 0.4 }}>{s.label}</div>
                            <div style={{ fontSize: isDesktop ? 22 : 15, fontWeight: 700, color: s.color || T.t1, marginTop: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.value}</div>
                        </div>
                    ))}
                </div>
                <div style={{ marginTop: 12, fontSize: 11, color: T.t3, display: "flex", alignItems: "flex-start", gap: 6 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 15, flexShrink: 0 }}>info</span>
                    Balance is program-wide (shared across all outlets). "Adjust Points" and the full member profile live in the Loyalty Admin Portal.
                </div>
            </SectionCard>
            <DataTableCard
                id="loy-member-activity" title="Member Activity"
                subtitle="Recent point transactions for this member"
                columns={columns} rows={memberActivity.rows} searchPlaceholder="Search detail"
                gaTable="member_activity" mobileCard={mobileCard}
                emptyMsg="No activity recorded for this member."
                serverPagination={{
                    page: memberActivity.page, pageSize: memberActivity.pageSize, totalElements: memberActivity.totalElements,
                    loading: memberActivity.loading, onPageChange: memberActivity.setPage, onPageSizeChange: memberActivity.setPageSize,
                }}
                serverSearch={{ value: memberActivity.search, onChange: memberActivity.setSearch }}
                exportConfig={{
                    title: `Member Activity — ${member.name}`, filename: `Loyalty_Member_${member.id}_${isoDate(new Date())}`,
                    // This log is fetched by loyaltyCustomerId only — no locationId param exists
                    // (balance/activity are program-wide, LOY-RPT-P-5.3) — so an "All outlets"
                    // export would just repeat identical rows per outlet. Suppress that option
                    // rather than offer a per-outlet split that can't actually mean anything.
                    outletScoped: false,
                    buildExport: async () => {
                        const all = await memberActivityExportFetch();
                        return {
                            headers: ["Date/Time", "Type", "Points", "Balance After", "Location", "Detail"],
                            rows: all.map((a) => [fmtDateTime(a.dateTime), TYPE_LABELS[a.type], roundNum(a.points), roundNum(a.balanceAfter), a.location, a.detail]),
                        };
                    },
                }}
            />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI GRID
   ═══════════════════════════════════════════════════════════════════════════ */
function KpiGrid({ data, showDelta }: { data: LoyaltyData; showDelta: boolean }) {
    const isDesktop = useIsDesktop();
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: isDesktop ? 12 : 8 }}>
            <div style={isDesktop ? { display: "flex", gap: 12, flexWrap: "wrap" } : { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {data.kpis.map((k) => <KPICard key={k.key} kpi={k} priorLabel={data.priorLabel} showDelta={showDelta} />)}
            </div>
            {data.secondaryKpis.length > 0 && (
                <div style={isDesktop ? { display: "flex", gap: 12, flexWrap: "wrap" } : { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {data.secondaryKpis.map((k) => <KPICard key={k.key} kpi={k} priorLabel={data.priorLabel} showDelta={showDelta} />)}
                    {isDesktop && data.secondaryKpis.length < 4 && Array.from({ length: 4 - data.secondaryKpis.length }).map((_, i) => <div key={`sp${i}`} style={{ flex: "1 1 130px", minWidth: 130 }} />)}
                </div>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SHIMMERS
   ═══════════════════════════════════════════════════════════════════════════ */
function DesktopShimmer() {
    return (
        <div className="rpt-page">
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} style={{ flex: 1, minWidth: 150, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 18, boxShadow: T.cardShadow }}>
                        <Bone h={12} w={80} mb={12} /><Bone h={26} w={100} mb={8} /><Bone h={12} w={60} />
                    </div>
                ))}
            </div>
            <div style={{ display: "flex", gap: 16 }}>
                {[0, 1].map((i) => (
                    <div key={i} style={{ flex: 1, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow }}>
                        <Bone h={16} w={160} mb={16} /><Bone h={220} r={10} />
                    </div>
                ))}
            </div>
            {[0].map((i) => (
                <div key={i} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow }}>
                    <Bone h={16} w={180} mb={16} />
                    {Array.from({ length: 6 }).map((_, r) => <Bone key={r} h={14} mb={10} />)}
                </div>
            ))}
        </div>
    );
}
function MobileShimmer() {
    return (
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 88px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: "12px 10px", boxShadow: T.shadow }}>
                        <Bone h={8} w={44} mb={6} /><Bone h={16} />
                    </div>
                ))}
            </div>
            {[0, 1].map((i) => (
                <div key={i} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 14, marginBottom: 8, boxShadow: T.shadow }}>
                    <Bone h={13} w={140} mb={12} /><Bone h={180} r={10} />
                </div>
            ))}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED REPORT BODY
   ═══════════════════════════════════════════════════════════════════════════ */
function useLoyaltyData(period: string, outletId: string | null, sym: string, summary: LoyaltySummary | null) {
    return useMemo(() => buildLoyaltyData(period, outletId, sym, summary), [period, outletId, sym, summary]);
}

function ReportBody({ data, sym, showDelta, isDesktop, onOpenMember, flowSeries, flowBucketNote, flowIsEmpty, growthSeries, growthBucketNote, topMembers, topMembersExportFetch, breakdown, breakdownIsEmpty, activityLog, activityLogExportFetch, attributedRevenue, attributedRevenueDelta, memberOrderCount, loyaltyAov, nonAov, aovLift }: {
    data: LoyaltyData; sym: string; showDelta: boolean; isDesktop: boolean;
    onOpenMember: (id: string, name: string) => void;
    flowSeries: { label: string; issued: number; redeemed: number }[]; flowBucketNote: string; flowIsEmpty: boolean;
    growthSeries: { label: string; newMembers: number; cumulative: number }[]; growthBucketNote: string;
    topMembers: TopMembersState; topMembersExportFetch: () => Promise<LoyMember[]>;
    breakdown: { type: string; points: number; color: string }[]; breakdownIsEmpty: boolean;
    activityLog: ActivityLogState; activityLogExportFetch: () => Promise<LoyActivity[]>;
    attributedRevenue: number; attributedRevenueDelta: number | null; memberOrderCount: number;
    loyaltyAov: number | null; nonAov: number | null; aovLift: number | null;
}) {
    return (
        <>
            <KpiGrid data={data} showDelta={showDelta} />
            {isDesktop ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <PointsFlowChart flowSeries={flowSeries} bucketNote={flowBucketNote} isEmpty={flowIsEmpty} />
                    <TxnBreakdownChart breakdown={breakdown} isEmpty={breakdownIsEmpty} />
                </div>
            ) : (
                <>
                    <PointsFlowChart flowSeries={flowSeries} bucketNote={flowBucketNote} isEmpty={flowIsEmpty} />
                    <TxnBreakdownChart breakdown={breakdown} isEmpty={breakdownIsEmpty} />
                </>
            )}
            <MembersGrowthChart growthSeries={growthSeries} bucketNote={growthBucketNote} />
            <LoyaltyValueSection sym={sym} showDelta={showDelta} priorLabel={data.priorLabel} attributedRevenue={attributedRevenue} attributedRevenueDelta={attributedRevenueDelta} memberOrderCount={memberOrderCount} loyaltyAov={loyaltyAov} nonAov={nonAov} lift={aovLift} />
            <TopMembersTable topMembers={topMembers} sym={sym} onOpenMember={(m) => onOpenMember(m.id, m.name)} exportFetch={topMembersExportFetch} />
            <ActivityLog activityLog={activityLog} onOpenMember={onOpenMember} exportFetch={activityLogExportFetch} />
        </>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DESKTOP
   ═══════════════════════════════════════════════════════════════════════════ */
function LoyaltyDesktop({ period, setPeriod, outletId, setOutletId, branches, branchesLoading, summary, summaryLoading, flowTrend, flowLoading, growthTrend, growthLoading, topMembers, topMembersExportFetch, breakdownData, breakdownLoading, activityLog, activityLogExportFetch, revenueData, revenueLoading, aovData, aovLoading, token, startDate, endDate }: any) {
    const sym = useCurrencySymbol();
    const navigate = useNavigate();
    const data = useLoyaltyData(period, outletId, sym, summary);
    const flow = useMemo(() => buildFlowSeriesFromTrend(flowTrend), [flowTrend]);
    const growth = useMemo(() => buildGrowthSeriesFromTrend(growthTrend), [growthTrend]);
    const breakdown = useMemo(() => buildBreakdownFromApi(breakdownData), [breakdownData]);
    const setTopBar = useDesktopTopBar();
    const [member, setMember] = useState<LoyMember | null>(null);
    const showDelta = showDeltaForPeriod(period, startDate, endDate);
    const loading = (!outletId && branchesLoading) || summaryLoading || flowLoading || growthLoading || breakdownLoading || revenueLoading || aovLoading || topMembers.initialLoading || activityLog.initialLoading;

    useEffect(() => { setMember(null); }, [period, outletId]);

    const closeMember = useCallback(() => setMember(null), []);

    useEffect(() => {
        setTopBar({
            breadcrumbs: member ? "Marketing › Loyalty" : "Marketing",
            title: member ? member.name : "Loyalty",
            dateFilters: member ? undefined : { value: period, onChange: (v: string) => { setPeriod(v); track("loyalty_filter_applied", { filter_type: "date_range", filter_value: v }); } },
            extraContent: member ? null : <OutletFilter value={outletId} onChange={setOutletId} branches={branches} loading={branchesLoading} />,
            leftAddon: member ? (
                <div onClick={closeMember} style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginRight: 4 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
                </div>
            ) : null,
        });
    }, [setTopBar, period, outletId, branches, branchesLoading, member, closeMember]);

    const openMember = useCallback((id: string, name: string) => {
        track("loyalty_member_opened", { member_id: id, member_name: name });
        // Rows can come from Activity Log, which spans far more members than whatever
        // page Top Members has loaded — a lookup miss there (or in the mock list) must
        // still open the drill-in. MemberDetail fetches the authoritative profile via
        // useMemberProfile(id, ...); this stub is only the placeholder shown until it lands.
        const m = topMembers.members.find((x: LoyMember) => x.id === id) ?? data.members.find((x) => x.id === id)
            ?? { id, name, contact: "", channel: "", balance: 0, lifetimeEarned: 0, lifetimeRedeemed: 0, orders: 0, lastActivity: "" };
        setMember(m); window.scrollTo({ top: 0 });
    }, [topMembers.members, data.members]);

    return (
        <DesktopLayout>
            {loading ? <DesktopShimmer /> : (
                <div className="rpt-page">
                    {member
                        ? <MemberDetail member={member} sym={sym} isDesktop onOpenAdmin={() => navigate(`/loyalty/members/${member.id}`)} token={token} />
                        : <ReportBody data={data} sym={sym} showDelta={showDelta} isDesktop onOpenMember={openMember} flowSeries={flow.flowSeries} flowBucketNote={flow.bucketNote} flowIsEmpty={flow.isEmpty} growthSeries={growth.growthSeries} growthBucketNote={growth.bucketNote} topMembers={topMembers} topMembersExportFetch={topMembersExportFetch} breakdown={breakdown.breakdown} breakdownIsEmpty={breakdown.isEmpty} activityLog={activityLog} activityLogExportFetch={activityLogExportFetch} attributedRevenue={revenueData?.attributedRevenue ?? 0} attributedRevenueDelta={revenueData?.attributedRevenueDelta ?? null} memberOrderCount={revenueData?.memberOrderCount ?? 0} loyaltyAov={aovData?.loyaltyMemberAov ?? null} nonAov={aovData?.nonMemberAov ?? null} aovLift={aovData?.aovLiftPercentage ?? null} />}
                </div>
            )}
        </DesktopLayout>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE
   ═══════════════════════════════════════════════════════════════════════════ */
function LoyaltyMobile({ period, setPeriod, outletId, setOutletId, branches, branchesLoading, summary, summaryLoading, flowTrend, flowLoading, growthTrend, growthLoading, topMembers, topMembersExportFetch, breakdownData, breakdownLoading, activityLog, activityLogExportFetch, revenueData, revenueLoading, aovData, aovLoading, token, startDate, endDate }: any) {
    const sym = useCurrencySymbol();
    const navigate = useNavigate();
    const data = useLoyaltyData(period, outletId, sym, summary);
    const flow = useMemo(() => buildFlowSeriesFromTrend(flowTrend), [flowTrend]);
    const growth = useMemo(() => buildGrowthSeriesFromTrend(growthTrend), [growthTrend]);
    const breakdown = useMemo(() => buildBreakdownFromApi(breakdownData), [breakdownData]);
    const setTopBar = useDesktopTopBar();
    const [member, setMember] = useState<LoyMember | null>(null);
    const showDelta = showDeltaForPeriod(period, startDate, endDate);
    const loading = (!outletId && branchesLoading) || summaryLoading || flowLoading || growthLoading || breakdownLoading || revenueLoading || aovLoading || topMembers.initialLoading || activityLog.initialLoading;
    useSwipeBack();
    useDrillHistoryBack(member != null, () => setMember(null));

    useEffect(() => { setMember(null); }, [period, outletId]);

    useEffect(() => {
        setTopBar({
            title: member ? member.name : "Loyalty",
            dateFilters: member ? undefined : { value: period, onChange: (v: string) => { setPeriod(v); track("loyalty_filter_applied", { filter_type: "date_range", filter_value: v }); } },
            onBack: member ? () => setMember(null) : undefined,
            hideOutlet: !!member,
        });
    }, [setTopBar, period, member]);

    const openMember = useCallback((id: string, name: string) => {
        track("loyalty_member_opened", { member_id: id, member_name: name });
        // Rows can come from Activity Log, which spans far more members than whatever
        // page Top Members has loaded — a lookup miss there (or in the mock list) must
        // still open the drill-in. MemberDetail fetches the authoritative profile via
        // useMemberProfile(id, ...); this stub is only the placeholder shown until it lands.
        const m = topMembers.members.find((x: LoyMember) => x.id === id) ?? data.members.find((x) => x.id === id)
            ?? { id, name, contact: "", channel: "", balance: 0, lifetimeEarned: 0, lifetimeRedeemed: 0, orders: 0, lastActivity: "" };
        setMember(m); window.scrollTo({ top: 0 });
    }, [topMembers.members, data.members]);

    return (
        <div style={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", background: T.bg }}>
            <TopBar variant="mobile" />
            {loading ? <MobileShimmer /> : (
                <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 88px", display: "flex", flexDirection: "column", gap: 12 }}>
                    {member
                        ? <MemberDetail member={member} sym={sym} isDesktop={false} onOpenAdmin={() => navigate(`/loyalty/members/${member.id}`)} token={token} />
                        : <>
                            <OutletFilter value={outletId} onChange={setOutletId} branches={branches} loading={branchesLoading} compact />
                            <ReportBody data={data} sym={sym} showDelta isDesktop={false} onOpenMember={openMember} flowSeries={flow.flowSeries} flowBucketNote={flow.bucketNote} flowIsEmpty={flow.isEmpty} growthSeries={growth.growthSeries} growthBucketNote={growth.bucketNote} topMembers={topMembers} topMembersExportFetch={topMembersExportFetch} breakdown={breakdown.breakdown} breakdownIsEmpty={breakdown.isEmpty} activityLog={activityLog} activityLogExportFetch={activityLogExportFetch} attributedRevenue={revenueData?.attributedRevenue ?? 0} attributedRevenueDelta={revenueData?.attributedRevenueDelta ?? null} memberOrderCount={revenueData?.memberOrderCount ?? 0} loyaltyAov={aovData?.loyaltyMemberAov ?? null} nonAov={aovData?.nonMemberAov ?? null} aovLift={aovData?.aovLiftPercentage ?? null} />
                        </>}
                </div>
            )}
            <BottomNav />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENTRY POINT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function LoyaltyReport() {
    const isDesktop = useIsDesktop();
    const [period, setPeriod] = useSharedReportDateFilter("Today");
    const [outletId, setOutletId] = useState<string | null>(null);

    const { auth } = useAuth();
    const { selectedBranch } = useBranch();
    const token = useMemo(() => findToken(auth), [auth]);
    const seedLocationId = (selectedBranch as any)?.id ?? (selectedBranch as any)?.locationId ?? null;

    const { branches, loading: branchesLoading } = useOutletBranches(seedLocationId, token);

    // Default the report's own outlet filter to the globally selected branch once the
    // real branch list loads; the user can still switch outlets from within the report.
    useEffect(() => {
        if (outletId || branches.length === 0) return;
        const match = branches.find((b) => b.id === seedLocationId);
        setOutletId((match ?? branches[0]).id);
    }, [branches, outletId, seedLocationId]);

    const { startDate, endDate } = useMemo(() => {
        const today = isoDate(new Date());
        const yesterday = isoDate(_addDays(new Date(), -1));
        return getStartEndFromFilter(period, today, yesterday) ?? { startDate: null, endDate: null };
    }, [period]);

    /* What every loyalty endpoint receives as `locationId`. "All outlets" sends
       each branch id comma separated (the endpoints bind List<String>); any
       other selection sends that single id. */
    const locationParam = useMemo(() => {
        if (outletId !== ALL_OUTLETS) return outletId;
        const ids = branches.map((b) => b.id).filter(Boolean);
        return ids.length ? ids.join(",") : null;
    }, [outletId, branches]);

    const isToday = period === "Today";
    const { summary, loading: summaryLoading } = useLoyaltySummary(locationParam, startDate, endDate, isToday, PROGRAM.dollarValuePerPoint, token);
    const { trend: flowTrend, loading: flowLoading } = usePointsFlowTrend(locationParam, startDate, endDate, isToday, token);
    const { trend: growthTrend, loading: growthLoading } = useMembersGrowthTrend(locationParam, startDate, endDate, isToday, token);
    const { breakdown: breakdownData, loading: breakdownLoading } = useTransactionBreakdown(locationParam, startDate, endDate, isToday, token);
    const topMembers = useTopMembers(locationParam, startDate, endDate, isToday, token);
    const topMembersSortDir = topMembers.sortDir;
    const topMembersSearch = topMembers.search;
    // The "All outlets" export loop (ReportSectionExportButton -> exportTableMultiOutlet)
    // calls this once per accessible outlet, passing that outlet's id as `locationId` — it
    // must override the report's own selected outlet, or every outlet's export silently
    // re-fetches the currently-selected outlet's data and all rows come out identical.
    const topMembersExportFetch = useCallback(
        (locationId?: string) => fetchAllTopMembersForExport(locationId ?? locationParam, startDate, endDate, isToday, topMembersSortDir, topMembersSearch, token),
        [locationParam, startDate, endDate, isToday, topMembersSortDir, topMembersSearch, token],
    );
    const activityLog = useActivityLog(locationParam, startDate, endDate, isToday, token);
    const activityLogTypeFilter = activityLog.typeFilter;
    const activityLogSearch = activityLog.search;
    const activityLogSortBy = activityLog.sortBy;
    const activityLogSortDir = activityLog.sortDir;
    const activityLogExportFetch = useCallback(
        (locationId?: string) => fetchAllActivityLogForExport(locationId ?? locationParam, startDate, endDate, isToday, activityLogTypeFilter, activityLogSearch, activityLogSortBy, activityLogSortDir, token),
        [locationParam, startDate, endDate, isToday, activityLogTypeFilter, activityLogSearch, activityLogSortBy, activityLogSortDir, token],
    );
    const { data: revenueData, loading: revenueLoading } = useAttributedRevenue(locationParam, startDate, endDate, isToday, token);
    const { data: aovData, loading: aovLoading } = useAovComparison(locationParam, startDate, endDate, isToday, token);

    useEffect(() => { track("loyalty_report_viewed", { date_range: period, outlet_scope: outletId ?? "all" }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const props = { period, setPeriod, outletId, setOutletId, branches, branchesLoading, summary, summaryLoading, flowTrend, flowLoading, growthTrend, growthLoading, topMembers, topMembersExportFetch, breakdownData, breakdownLoading, activityLog, activityLogExportFetch, revenueData, revenueLoading, aovData, aovLoading, token, startDate, endDate };
    return isDesktop ? <LoyaltyDesktop {...props} /> : <LoyaltyMobile {...props} />;
}
